﻿import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Camera, Link as LinkIcon, Play, Square, Eye, AlertTriangle, Car, Search, X, Layers, CheckCircle, Clock, Zap, Tv, Plus, Wifi, LayoutGrid, Power, User, FileText, RefreshCw } from 'lucide-react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { analyzeFaceMesh, createBlinkDetector, makeLumaVarianceSampler } from '../lib/faceMesh';
import { createFaceVoteBuffer } from '../lib/faceVoteBuffer';
import { useApp } from '../contexts/AppContext';
import { getIntelLabel, getIntelColor } from '../lib/statusLabel';
import { supabase } from '../lib/supabase';
import AutoScanPlayer from '../components/AutoScanPlayer';
import CctvWall, { CctvCell } from '../components/CctvWall';
import { buildTiles, MAX_LIVE, BACKUP_CLIPS } from '../data/cctvFeeds';
import { logScanResult, logActivity } from '../lib/activityLog';
import {
  callHFAnalyze, enrichScanResult, renderAllBboxes,
  dispatchAlerts, VEHICLE_LABELS as AI_VEHICLE_LABELS,
  WEAPON_LABELS as AI_WEAPON_LABELS,
  searchPenduduk,
} from '../lib/aiScanner';

import { cleanEnv } from '../lib/envClean';
const HF_URL = cleanEnv(import.meta.env.VITE_HF_SPACE_URL) || 'https://quantum2026ops-kes-cybercity.hf.space';
// Tidak ada salinan lokal VEHICLE_LABELS. Dulu baris ini menyalin dari
// aiScanner, dan salinan itu harus diperbarui terpisah tiap kali kelas
// berubah — akar yang membuat kelas Indonesia tidak tampil. Pakai
// AI_VEHICLE_LABELS (impor di atas), satu sumber.
const FACE_MATCH_THRESHOLD = 0.38; // lower than HF's 0.45 for better recall
const ALERT_DEDUP_MS = 30000;      // per-NIK: don't insert alert for same person within this window
// Pacing scan CCTV: poll murah tiap 200 ms, scan baru mulai begitu yang lama selesai (kunci scanningRef)
// asal sudah >= 600 ms sejak scan sebelumnya mulai. Dulu setInterval 2000 ms: periode 2 s (4 s bila satu scan >2 s).
const SCAN_TICK_MS = 200;
const SCAN_MIN_PERIOD_MS = 600;
const SCAN_NOFACE_FORCE_MS = 10000; // mesh aktif tapi tanpa wajah: tetap kirim tiap 10 s (kendaraan/wajah yang luput mesh)
// Sisi terpanjang frame yang dikirim ke HF (webcam 1280x720 -> 640x360, ~29 KB bukan ~145 KB). Terukur dari VM ke HF:
// tiap ~15 KB tambahan memakan 1 RTT (~220 ms) karena TCP slow-start; 106 KB = 904 ms, 30 KB = 490 ms. Naikkan ke 960 bila kendaraan/senjata kecil luput.
const SCAN_MAX_SIDE = 640;

// ── Helpers ───────────────────────────────────────────────────────────────────

function bboxToRel([x1, y1, x2, y2], imgW, imgH) {
  return { x: x1 / imgW, y: y1 / imgH, w: (x2 - x1) / imgW, h: (y2 - y1) / imgH };
}

function cosineSim(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return 0;
  let dot = 0, na = 0, nb = 0;
  for (let i = 0; i < a.length; i++) { dot += a[i]*b[i]; na += a[i]*a[i]; nb += b[i]*b[i]; }
  if (na === 0 || nb === 0) return 0;
  return dot / Math.sqrt(na * nb);
}

// Gambar mesh wajah 478 titik (tesselation + kontur + titik 3D) untuk SATU wajah
// pada ctx dengan warna rgb tertentu. Dipakai baik oleh loop live maupun saat
// perekaman/capture supaya titik-titik yang tampil benar-benar ikut terekam.
function drawFaceMeshLayers(ctx, landmarks, w, h, colorRGB = '0,212,255') {
  if (!landmarks || !landmarks.length) return;
  // Layer a: jaring tesselation (mesh akurasi tinggi, ~2600 segmen).
  ctx.strokeStyle = `rgba(${colorRGB},0.22)`;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  for (const { start, end } of FaceLandmarker.FACE_LANDMARKS_TESSELATION) {
    const s = landmarks[start], e = landmarks[end];
    ctx.moveTo(s.x * w, s.y * h); ctx.lineTo(e.x * w, e.y * h);
  }
  ctx.stroke();

  // Layer b: kontur fitur (oval wajah + mata + alis + bibir + iris).
  ctx.strokeStyle = `rgba(${colorRGB},0.85)`;
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  for (const group of [FaceLandmarker.FACE_LANDMARKS_FACE_OVAL,
                        FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE,
                        FaceLandmarker.FACE_LANDMARKS_LEFT_EYEBROW, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYEBROW,
                        FaceLandmarker.FACE_LANDMARKS_LIPS,
                        FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS, FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS]) {
    for (const { start, end } of group) {
      const s = landmarks[start], e = landmarks[end];
      ctx.moveTo(s.x * w, s.y * h); ctx.lineTo(e.x * w, e.y * h);
    }
  }
  ctx.stroke();

  // Layer c: 478 titik landmark — ukuran & opasitas ikut kedalaman (z) → kesan 3D.
  for (let li = 0; li < landmarks.length; li++) {
    const p = landmarks[li];
    const depth = Math.min(Math.max(0.5 - (p.z ?? 0) * 6, 0), 1); // 0..1, dekat→1
    ctx.globalAlpha = 0.30 + depth * 0.60;
    ctx.fillStyle = `rgb(${colorRGB})`;
    ctx.beginPath();
    ctx.arc(p.x * w, p.y * h, 0.6 + depth * 1.1, 0, 6.283);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// HUD analitik mesh: kualitas, pose, mata, liveness, dan peringatan, digambar
// langsung di canvas overlay (tanpa re-render React supaya ringan).
function drawMeshHud(ctx, q, live, w, h) {
  const issues = q.issues.slice(0, 2);
  const bh = 50 + issues.length * 13;
  // Kiri bawah, supaya tidak menumpuk lencana LIVE/CCTV/SCANNING di kiri atas (permintaan Andra 19 Okt)
  const bx = 10, by = h - bh - 10, bw = 200;
  const scoreColor = q.qualityScore >= 75 ? '#00ff88' : q.qualityScore >= 50 ? '#ffd60a' : '#ff2d55';

  ctx.fillStyle = 'rgba(2,6,14,0.78)';
  ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, 6); ctx.fill();
  ctx.strokeStyle = scoreColor + '55'; ctx.lineWidth = 1; ctx.stroke();

  ctx.font = 'bold 12px "IBM Plex Mono", monospace';
  ctx.fillStyle = scoreColor;
  ctx.fillText(`KUALITAS ${q.qualityScore}`, bx + 9, by + 18);

  ctx.font = '9px "IBM Plex Mono", monospace';
  ctx.fillStyle = '#9fb4c8';
  ctx.fillText(`POSE ${q.poseClass}   MATA ${q.eyesOpen ? 'BUKA' : 'TUTUP'}`, bx + 9, by + 32);

  ctx.fillStyle = live.live ? '#00ff88' : '#7a8aa0';
  ctx.fillText(`LIVENESS ${live.live ? 'HIDUP' : 'CEK KEDIP'} (${live.blinks})`, bx + 9, by + 44);

  let yy = by + 57;
  ctx.fillStyle = '#ffb44d';
  for (const iss of issues) {
    ctx.fillText(`! ${iss}`, bx + 9, yy);
    yy += 13;
  }
}

function clientMatch(embedding, knownFaces) {
  if (!Array.isArray(embedding) || !embedding.length || !knownFaces?.length) {
    return { nik: null, sim: 0, person: null };
  }
  const MARGIN = 0.05; // tolak ID kalau kandidat-2 (orang beda) terlalu dekat
  let best = { nik: null, sim: -1, person: null };
  let secondSim = -1, secondNik = null;
  for (const kf of knownFaces) {
    if (!Array.isArray(kf.emb)) continue;
    const sim = cosineSim(embedding, kf.emb);
    if (sim > best.sim) { secondSim = best.sim; secondNik = best.nik; best = { nik: kf.nik, sim, person: kf }; }
    else if (sim > secondSim) { secondSim = sim; secondNik = kf.nik; }
  }
  // Ambigu → kembalikan tanpa nik supaya pemanggil menganggap tidak dikenal.
  if (best.nik && secondNik && secondNik !== best.nik
      && secondSim >= FACE_MATCH_THRESHOLD && (best.sim - secondSim) < MARGIN) {
    return { nik: null, sim: best.sim, person: null };
  }
  return best;
}

function calcAge(tanggal_lahir) {
  if (!tanggal_lahir) return null;
  const birth = new Date(tanggal_lahir);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() ||
     (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

function fmtTTL(tempat_lahir, tanggal_lahir) {
  const parts = [];
  if (tempat_lahir) parts.push(tempat_lahir);
  if (tanggal_lahir) {
    const d = new Date(tanggal_lahir);
    parts.push(d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }));
  }
  return parts.join(', ');
}

function nowTime() {
  return new Date().toLocaleTimeString('id-ID', { hour12: false });
}

function nowDate() {
  return new Date().toLocaleDateString('id-ID');
}

// Mask NIK like 6402******0001 for safe display (NIK = 16 digits)
function maskNik(nik) {
  if (!nik || typeof nik !== 'string') return '—';
  if (nik.length < 8) return nik;
  return nik.slice(0, 4) + '*'.repeat(Math.max(0, nik.length - 8)) + nik.slice(-4);
}

// ── CCTV Feed Components ──────────────────────────────────────────────────────
//
// CameraFeedCard + HLSPlayer were removed; FeedTab now uses AutoScanPlayer
// (../components/AutoScanPlayer) which handles HLS, video cycling, image
// cycling, bbox overlay, and per-cell auto-scan with vehicle/owner lookup.

const BatchStreamModal = ({ cameras, onClose, onImported }) => {
  const [text, setText]     = useState('');
  const [preview, setPreview] = useState([]);
  const [step, setStep]     = useState('input'); // 'input' | 'preview' | 'done'
  const [results, setResults] = useState(null);

  const parse = () => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const entries = lines.map(l => {
      const m = l.match(/^(.+?):\s*(https?:\/\/.+)$/);
      if (m) return { name: m[1].trim(), url: m[2].trim() };
      if (/^https?:\/\//i.test(l)) return { name: null, url: l };
      return null;
    }).filter(Boolean);
    setPreview(entries);
    setStep('preview');
  };

  const doImport = async () => {
    let ok = 0, fail = 0;
    for (const entry of preview) {
      const existing = entry.name
        ? cameras.find(c => c.nama?.toLowerCase() === entry.name.toLowerCase())
        : null;
      if (existing) {
        const { error } = await supabase.from('kamera').update({ stream_url: entry.url }).eq('id', existing.id);
        error ? fail++ : ok++;
      } else {
        const { error } = await supabase.from('kamera').insert({
          nama: entry.name || `Stream ${Date.now()}`,
          lokasi: 'HLS Stream',
          status: 'ONLINE',
          stream_url: entry.url,
        });
        error ? fail++ : ok++;
      }
    }
    setResults({ ok, fail });
    setStep('done');
    if (ok > 0) onImported();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-xl rounded-3xl overflow-hidden flex flex-col"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '80vh' }}>
        <div className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
            Batch Import Stream URL {step === 'preview' && <span style={{ color: 'var(--color-cyan)' }}>— {preview.length} URL</span>}
          </p>
          <button onClick={onClose}><X size={13} color="var(--text-dim)" /></button>
        </div>

        {step === 'input' && (
          <div className="flex-1 flex flex-col gap-4 p-5">
            <p className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
              Tempel URL satu per baris. Format opsional: <span style={{ color: 'var(--color-cyan)' }}>Nama Kamera: https://...m3u8</span>
            </p>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={8}
              placeholder={'CCTV Utara: https://server.com/live/cam1.m3u8\nhttps://server.com/live/cam2.m3u8'}
              className="w-full resize-none rounded-xl px-3 py-2.5 text-xs outline-none font-mono c-scroll"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }} />
            <button onClick={parse} disabled={!text.trim()}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black"
              style={{ background: 'var(--color-cyan)', color: '#02060e', opacity: text.trim() ? 1 : 0.5 }}>
              Preview Import
            </button>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-auto c-scroll p-4 space-y-2">
              {preview.map((e, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                  <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#ff2d55', animation: 'blink 1s infinite' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-bold truncate" style={{ color: 'var(--text-hi)' }}>{e.name || '(nama otomatis)'}</p>
                    <p className="text-[8px] font-mono truncate" style={{ color: 'var(--text-mute)' }}>{e.url}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 flex gap-2 shrink-0" style={{ borderTop: '1px solid var(--border-dim)' }}>
              <button onClick={() => setStep('input')}
                className="px-4 py-2 rounded-xl text-xs font-black"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
                Kembali
              </button>
              <button onClick={doImport}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-black"
                style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
                <Wifi size={12} /> Import {preview.length} Stream
              </button>
            </div>
          </div>
        )}

        {step === 'done' && results && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8">
            <div className="grid grid-cols-2 gap-4 w-full">
              <div className="flex flex-col items-center gap-2 p-4 rounded-2xl" style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>
                <p className="text-3xl font-black font-mono" style={{ color: 'var(--color-green)' }}>{results.ok}</p>
                <p className="text-[8px] font-black uppercase" style={{ color: 'var(--color-green)' }}>{t('success_label')}</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-2xl" style={{ background: 'rgba(255,45,85,0.08)', border: '1px solid rgba(255,45,85,0.2)' }}>
                <p className="text-3xl font-black font-mono" style={{ color: '#ff2d55' }}>{results.fail}</p>
                <p className="text-[8px] font-black uppercase" style={{ color: '#ff2d55' }}>{t('failed_label')}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black"
              style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
              <CheckCircle size={13} /> {t('btn_done') || (lang === 'id' ? 'Selesai' : 'Done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Andra 30 Okt 2026 (#274 opsi b): 3×3 dan 4×4 kembali untuk kamera terdaftar.
const COLS_OPTIONS = [
  { cols: 1, label: '1×1' },
  { cols: 2, label: '2×2' },
  { cols: 3, label: '3×3' },
  { cols: 4, label: '4×4' },
];

// Bila banyak sel menyala, scan bergiliran: satu sel per giliran (#280).
const SCAN_TURN_MS = 5000;

const SESSION_KEY_GRID = 'horus_grid_layout';

// Bawaan 3×3 (#275).
function loadGridCols() {
  try {
    const saved = parseInt(sessionStorage.getItem(SESSION_KEY_GRID), 10);
    if (saved >= 1 && saved <= 4) return saved;
    return 3;
  } catch { return 3; }
}

const FeedTab = ({ cameras, setCameras, onFaceDetected }) => {
  const { t, lang } = useApp();
  const [loading, setLoading]       = useState(!cameras?.length);
  const [cols, setCols]             = useState(loadGridCols);
  const [showBatch, setShowBatch]   = useState(false);
  const [addingNew, setAddingNew]   = useState(false);
  const [newForm, setNewForm]       = useState({ nama: '', lokasi: '', stream_url: '', rtsp_url: '' });
  const [selectedFile, setSelectedFile] = useState(null); // upload video/photo alternative
  const [saving, setSaving]         = useState(false);
  const fileInputRef = useRef(null);
  const [maximizedId, setMaximizedId] = useState(null);
  // Scan AI: saklar global + per kamera, bawaan OFF (#280).
  const [scanEnabled, setScanEnabled] = useState(false);
  const [camScan, setCamScan] = useState({});   // { [idKamera]: true }
  const [turnIdx, setTurnIdx] = useState(0);    // urutan giliran scan
  const [showScanList, setShowScanList] = useState(false);
  const [embeddingsLoaded, setEmbeddingsLoaded] = useState(false);
  const embeddingsRef = useRef([]); // [{nik, emb}] shared across all cells for client-side match

  // Jalur B — tangkap tab/layar (mis. YouTube) via getDisplayMedia. Stream lokal
  // sehingga bisa di-scan AI (tidak seperti iframe YouTube yang buram/tainted).
  const [screenStream, setScreenStream] = useState(null);
  const screenStreamRef = useRef(null);
  useEffect(() => { screenStreamRef.current = screenStream; }, [screenStream]);
  useEffect(() => () => { screenStreamRef.current?.getTracks().forEach(tk => tk.stop()); }, []);

  const stopScreenCapture = useCallback(() => {
    setScreenStream(prev => { prev?.getTracks().forEach(tk => tk.stop()); return null; });
  }, []);

  const startScreenCapture = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert(lang === 'id'
        ? 'Browser ini tidak mendukung tangkap tab/layar. Pakai Chrome/Edge terbaru di desktop.'
        : 'This browser does not support screen/tab capture. Use recent Chrome/Edge on desktop.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: false,
      });
      screenStreamRef.current?.getTracks().forEach(tk => tk.stop());
      // User klik "Stop sharing" di UI browser → auto-bersihkan cell.
      stream.getVideoTracks()[0]?.addEventListener('ended', () => setScreenStream(null));
      setScreenStream(stream);
    } catch (e) {
      if (e?.name !== 'NotAllowedError' && e?.name !== 'AbortError') {
        alert((lang === 'id' ? 'Tangkap layar gagal: ' : 'Screen capture failed: ') + (e?.message || e));
      }
    }
  }, [lang]);

  // Kamera ad-hoc untuk cell tangkapan layar (tidak disimpan ke DB).
  const screenCam = { id: 'screen-capture', nama: lang === 'id' ? 'YouTube / Tab' : 'YouTube / Tab', lokasi: lang === 'id' ? 'Tangkapan Layar' : 'Screen Capture' };

  // Satu sumber stream (URL sama) hanya tampil sekali di grid; kamera tanpa stream_url (playlist) tidak disaring.
  const seenUrl = new Set();
  const gridCams = cameras.filter(c => {
    const u = (c.stream_url || '').trim();
    if (!u) return true;
    if (seenUrl.has(u)) return false;
    seenUrl.add(u);
    return true;
  });
  // Sel sisa: CCTV publik, tiap sumber sekali dan bukan sumber kamera terdaftar (#275). Bila sumber habis, sel dibiarkan kosong, bukan diulang.
  const fillTiles = buildTiles(MAX_LIVE + BACKUP_CLIPS.length)
    .filter(tile => !seenUrl.has(tile.url))
    .slice(0, Math.max(0, cols * cols - gridCams.length - (screenStream ? 1 : 0)))
    .map((tile, j) => ({ ...tile, id: `CAM-${String(gridCams.length + (screenStream ? 1 : 0) + j + 1).padStart(2, '0')}` }));

  // Giliran scan: hanya kamera yang menyala (kunci "id|id") dan hanya bila saklar global ON.
  const scanOnKey = [...(screenStream ? [screenCam.id] : []), ...gridCams.map(c => c.id)]
    .filter(id => camScan[id]).join('|');
  useEffect(() => {
    const n = scanOnKey ? scanOnKey.split('|').length : 0;
    if (!scanEnabled || n < 2) return undefined;
    const timer = setInterval(() => setTurnIdx(i => (i + 1) % n), SCAN_TURN_MS);
    return () => clearInterval(timer);
  }, [scanEnabled, scanOnKey]);
  const scanIds = scanEnabled && scanOnKey ? scanOnKey.split('|') : [];
  const turnId = scanIds.length ? scanIds[turnIdx % scanIds.length] : null;
  const scanProps = (id) => ({
    scanEnabled,
    scanTurn: turnId === String(id),
    cellScanOn: !!camScan[id],
    onCellScanToggle: () => setCamScan(m => ({ ...m, [id]: !m[id] })),
  });

  // Cold load
  useEffect(() => {
    let alive = true;
    if (!cameras?.length) {
      supabase.from('kamera').select('*').order('nama').then(({ data, error }) => {
        if (!alive) return;
        if (error) console.warn('[FeedTab cold load] fail:', error.message);
        if (data) setCameras(data);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Embeddings: initial load + realtime listener + 5min safety refresh.
  // Setiap CRUD foto wajah di Datacenter/Intel harus refleksi ke Live Feed
  // tanpa user perlu reload halaman.
  useEffect(() => {
    let alive = true;
    const reloadEmbeddings = async () => {
      const { data, error } = await supabase.from('penduduk')
        .select('nik, face_embedding')
        .not('face_embedding', 'is', null);
      if (!alive) return;
      if (error) { console.warn('[FeedTab reloadEmbeddings] fail:', error.message); return; }
      embeddingsRef.current = (data || []).map(p => ({ nik: p.nik, emb: p.face_embedding }));
      setEmbeddingsLoaded(true);
    };
    reloadEmbeddings();

    const channel = supabase
      .channel('feedtab-penduduk-embedding')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'penduduk' }, reloadEmbeddings)
      .subscribe();

    const interval = setInterval(reloadEmbeddings, 5 * 60 * 1000); // 300000ms = 5min safety net

    return () => {
      alive = false;
      clearInterval(interval);
      try { channel.unsubscribe(); } catch { /* noop */ }
      supabase.removeChannel(channel);
    };
  }, []);

  // Esc minimizes the maximized cell (matches user expectation from native fullscreen)
  useEffect(() => {
    if (!maximizedId) return;
    const onKey = (e) => { if (e.key === 'Escape') setMaximizedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximizedId]);

  // Generate kamera ID dari nama: ambil 3 huruf pertama uppercase + random suffix
  // Contoh: 'Senayan-001' -> 'CAM-SEN-7050'
  const generateKameraId = (nama) => {
    const slug = (nama || 'CAM').replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'CAM';
    const rnd = Math.floor(1000 + Math.random() * 9000);
    return `CAM-${slug}-${rnd}`;
  };

  const handleAddNew = async () => {
    if (!newForm.nama.trim()) return;
    setSaving(true);
    // Generate ID dengan retry kalau collision (rare)
    let attempt = 0;
    let lastError = null;
    while (attempt < 3) {
      const newId = generateKameraId(newForm.nama);

      // Build payload — support 3 modes:
      //   (a) selectedFile (video/photo upload) → playlist + storage upload
      //   (b) newForm.stream_url (HLS/RTSP) → stream_url field
      //   (c) neither → kamera placeholder, user atur source nanti
      const payload = {
        id: newId,
        nama: newForm.nama.trim(),
        lokasi: newForm.lokasi.trim() || t('nv_unknown_loc'),
        status: 'ONLINE',
        // Priority: HLS m3u8 (browser-playable) > RTSP/IP (perlu transcoder)
        stream_url: !selectedFile
          ? (newForm.stream_url.trim() || newForm.rtsp_url.trim() || null)
          : null,
      };

      // Upload file ke storage dulu kalau ada, biar URL ready saat insert kamera
      if (selectedFile) {
        const ext = (selectedFile.name.split('.').pop() || '').toLowerCase();
        const isImage = /^(jpg|jpeg|png|webp|gif|bmp)$/i.test(ext);
        const isVideo = /^(mp4|webm|m4v|mov|avi|mkv|wmv|flv|3gp|mpeg|mpg)$/i.test(ext);
        if (!isImage && !isVideo) {
          setSaving(false);
          alert(`Format .${ext} tidak didukung. Pakai video (mp4/mov/...) atau gambar (jpg/png/webp).`);
          return;
        }
        try {
          const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const path = `${newId}/${Date.now()}_${safeName}`;
          const { error: uploadErr } = await supabase.storage
            .from('cctv-videos').upload(path, selectedFile, { upsert: false, contentType: selectedFile.type });
          if (uploadErr) throw uploadErr;
          const { data: urlData } = supabase.storage.from('cctv-videos').getPublicUrl(path);
          payload.playlist = [{
            url: urlData.publicUrl,
            nama: selectedFile.name,
            size: selectedFile.size,
            kind: isImage ? 'image' : 'video',
            uploaded_at: new Date().toISOString(),
          }];
          payload.total_videos = 1;
        } catch (e) {
          setSaving(false);
          console.warn('[handleAddNew] upload fail:', e?.message);
          alert(`Upload file gagal: ${e?.message || 'unknown'}`);
          return;
        }
      }

      const { data, error } = await supabase.from('kamera').insert(payload).select().single();
      if (!error) {
        setSaving(false);
        if (data) setCameras(prev => [...prev, data]);
        setNewForm({ nama: '', lokasi: '', stream_url: '', rtsp_url: '' });
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setAddingNew(false);
        return;
      }
      lastError = error;
      // Hanya retry kalau duplicate id (PK collision), bukan error lain
      if (!/duplicate key|already exists|unique/i.test(error.message)) break;
      attempt++;
    }
    setSaving(false);
    console.warn('[FeedTab.handleAddNew] fail:', lastError?.message);
    alert(`${t('nv_cam_add_fail')}: ${lastError?.message}`);
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="h-full flex flex-col gap-3 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <Tv size={13} color="var(--color-cyan)" />
        <span className="text-[9px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
          {gridCams.length} {t('nv_cameras_count')}
        </span>
        <div className="flex-1" />
        {/* Auto-scan toggle */}
        <button onClick={() => setScanEnabled(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{
            background: scanEnabled ? 'rgba(0,255,136,0.12)' : 'rgba(71,85,105,0.15)',
            border: `1px solid ${scanEnabled ? 'rgba(0,255,136,0.35)' : 'var(--border-dim)'}`,
            color: scanEnabled ? 'var(--color-green)' : 'var(--text-mute)',
          }}
          title={scanEnabled ? t('nv_scan_on_title') : t('nv_scan_off_title')}>
          <Power size={9} /> {scanEnabled ? t('nv_scan_on') : t('nv_scan_off')}
        </button>
        <div className="relative">
          <button onClick={() => setShowScanList(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }}>
            <Eye size={9} /> {lang === 'id' ? 'Scan per kamera' : 'Scan per camera'} ({gridCams.filter(c => camScan[c.id]).length}/{gridCams.length})
          </button>
          {showScanList && (
            <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-30 w-64 max-h-64 overflow-y-auto c-scroll rounded-xl p-2 flex flex-col gap-1"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
              {gridCams.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-[10px] font-mono cursor-pointer" style={{ color: 'var(--text-mid)' }}>
                  <input type="checkbox" checked={!!camScan[c.id]} onChange={() => setCamScan(m => ({ ...m, [c.id]: !m[c.id] }))} />
                  <span className="truncate">{c.nama}</span>
                </label>
              ))}
              <p className="text-[8px]" style={{ color: 'var(--text-mute)' }}>
                {lang === 'id' ? 'Kamera menyala discan bergiliran, bukan serentak.' : 'Enabled cameras are scanned in turns, not at once.'}
              </p>
            </div>
          )}
        </div>
        <div className="w-px h-4" style={{ background: 'var(--border-dim)' }} />
        {/* Grid cols */}
        <div className="flex gap-1">
          {COLS_OPTIONS.map(({ cols: c, label }) => (
            <button key={c} onClick={() => { setCols(c); try { sessionStorage.setItem(SESSION_KEY_GRID, String(c)); } catch {} }}
              className="px-2.5 py-1.5 rounded-lg text-[8px] font-black transition-all"
              style={{ background: cols === c ? 'rgba(0,212,255,0.15)' : 'var(--color-card)', border: `1px solid ${cols === c ? 'rgba(0,212,255,0.4)' : 'var(--border-dim)'}`, color: cols === c ? 'var(--color-cyan)' : 'var(--text-mute)' }}>
              {label}
            </button>
          ))}
        </div>
        <div className="w-px h-4" style={{ background: 'var(--border-dim)' }} />
        <button onClick={() => setShowBatch(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: 'var(--color-cyan)' }}>
          <LayoutGrid size={9} /> {t('nv_batch_url')}
        </button>
        <button onClick={() => setAddingNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
          <Plus size={9} /> {t('nv_add_camera')}
        </button>
        {/* Jalur B — putar & scan dari tab/layar (mis. YouTube) */}
        <button onClick={screenStream ? stopScreenCapture : startScreenCapture}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{
            background: screenStream ? 'rgba(255,45,85,0.10)' : 'rgba(255,45,85,0.08)',
            border: `1px solid ${screenStream ? 'rgba(255,45,85,0.5)' : 'rgba(255,45,85,0.2)'}`,
            color: '#ff6b7a',
          }}
          title={lang === 'id'
            ? 'Putar & scan video dari tab lain (mis. YouTube). Pilih tab yang sedang memutar video saat diminta.'
            : 'Play & scan video from another tab (e.g. YouTube). Pick the tab playing the video when prompted.'}>
          {screenStream
            ? <><X size={9} /> {lang === 'id' ? 'Stop Tab' : 'Stop Tab'}</>
            : <><Tv size={9} /> {lang === 'id' ? 'Putar dari Tab' : 'Play from Tab'}</>}
        </button>
      </div>

      {/* Add new camera inline form — 2 options: paste URL OR upload file */}
      {addingNew && (
        <div className="flex flex-col gap-2 p-3 rounded-2xl shrink-0"
          style={{ background: 'var(--color-card)', border: '1px solid rgba(0,255,136,0.2)' }}>
          <div className="flex items-center gap-2">
            <input value={newForm.nama} onChange={e => setNewForm(f => ({ ...f, nama: e.target.value }))}
              placeholder={t('nv_cam_name_ph')}
              className="rounded-xl px-3 py-2 text-xs outline-none w-32"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }} />
            <input value={newForm.lokasi} onChange={e => setNewForm(f => ({ ...f, lokasi: e.target.value }))}
              placeholder={t('nv_cam_loc_ph')}
              className="rounded-xl px-3 py-2 text-xs outline-none w-32"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }} />
            <input value={newForm.stream_url} onChange={e => setNewForm(f => ({ ...f, stream_url: e.target.value, rtsp_url: '' }))}
              placeholder={lang === 'id' ? 'https://... .m3u8 (HLS) atau WebRTC/WHEP (opsional)' : 'https://... .m3u8 (HLS) or WebRTC/WHEP (optional)'}
              disabled={!!selectedFile || !!newForm.rtsp_url}
              className="flex-1 rounded-xl px-3 py-2 text-xs outline-none font-mono"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--border-dim)',
                color: 'var(--text-mid)',
                opacity: (selectedFile || newForm.rtsp_url) ? 0.4 : 1,
              }} />
            <button onClick={handleAddNew} disabled={saving || !newForm.nama.trim()}
              className="px-4 py-2 rounded-xl text-xs font-black shrink-0"
              style={{ background: 'var(--color-green)', color: '#02060e', opacity: saving || !newForm.nama.trim() ? 0.6 : 1 }}>
              {saving ? t('dc_saving') : t('dc_save')}
            </button>
            <button onClick={() => { setAddingNew(false); setSelectedFile(null); setNewForm({ nama: '', lokasi: '', stream_url: '', rtsp_url: '' }); }}
              className="px-3 py-2 rounded-xl text-xs font-black shrink-0"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
              {t('dc_cancel')}
            </button>
          </div>

          {/* Row 2: separator "atau" untuk RTSP/IP option (browser tidak bisa play
              langsung — perlu transcoder eksternal seperti go2rtc / MediaMTX). */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px" style={{ background: 'var(--border-dim)' }} />
            <span className="text-[8px] font-mono uppercase tracking-widest" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'atau RTSP / IP camera' : 'or RTSP / IP camera'}
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-dim)' }} />
          </div>

          <div className="flex items-center gap-2">
            <input value={newForm.rtsp_url}
              onChange={e => setNewForm(f => ({ ...f, rtsp_url: e.target.value, stream_url: '' }))}
              placeholder={lang === 'id'
                ? 'rtsp://192.168.1.x:554/stream  atau  IP camera lokal'
                : 'rtsp://192.168.1.x:554/stream  or  local IP camera'}
              disabled={!!selectedFile || !!newForm.stream_url}
              className="flex-1 rounded-xl px-3 py-2 text-xs outline-none font-mono"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid rgba(255,107,43,0.25)',
                color: 'var(--text-mid)',
                opacity: (selectedFile || newForm.stream_url) ? 0.4 : 1,
              }} />
            <span className="text-[9px] font-mono shrink-0 px-2 py-1 rounded"
              style={{ background: 'rgba(255,214,10,0.10)', border: '1px solid rgba(255,214,10,0.3)', color: '#ffd60a' }}>
              ⚠ {lang === 'id' ? 'butuh transcoder' : 'needs transcoder'}
            </span>
          </div>
          {newForm.rtsp_url && !newForm.stream_url && (
            <p className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id'
                ? '↑ Browser tidak bisa play RTSP langsung. Jika gateway (go2rtc / MediaMTX) sudah dikonfigurasi via VITE_STREAM_GATEWAY, RTSP otomatis dirutekan ke WebRTC. Jika belum, setup gateway dulu atau paste URL HLS / WebRTC di field atas.'
                : '↑ Browser cannot play RTSP directly. If a gateway (go2rtc / MediaMTX) is configured via VITE_STREAM_GATEWAY, RTSP is auto-routed to WebRTC. Otherwise set up the gateway first, or paste an HLS / WebRTC URL in the field above.'}
            </p>
          )}

          {/* Row 3: divider "atau" + upload-file option */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px" style={{ background: 'var(--border-dim)' }} />
            <span className="text-[8px] font-mono uppercase tracking-widest" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'atau unggah file' : 'or upload file'}
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-dim)' }} />
          </div>

          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="video/*,image/*" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setSelectedFile(f);
              }} />
            <button onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black"
              style={{ background: 'rgba(124,77,255,0.10)', border: '1px solid rgba(124,77,255,0.3)', color: '#7c4dff' }}>
              <Upload size={11} /> {lang === 'id' ? 'Pilih Video / Foto' : 'Choose Video / Photo'}
            </button>
            {selectedFile ? (
              <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl truncate"
                style={{ background: 'var(--color-surface)', border: '1px solid rgba(124,77,255,0.25)' }}>
                <span className="text-xs font-mono truncate" style={{ color: 'var(--text-hi)' }}>{selectedFile.name}</span>
                <span className="text-[9px] font-mono shrink-0" style={{ color: 'var(--text-mute)' }}>
                  ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                </span>
                <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="ml-auto rounded p-0.5 hover:opacity-70 shrink-0">
                  <X size={11} color="var(--text-dim)" />
                </button>
              </div>
            ) : (
              <span className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
                {lang === 'id'
                  ? 'mp4, mov, mkv, webm · jpg, png, webp · max 500 MB'
                  : 'mp4, mov, mkv, webm · jpg, png, webp · max 500 MB'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Camera grid */}
      {cameras.length === 0 && !screenStream ? (
        /* Belum ada kamera terdaftar: dinding siaran publik Jakarta + rekaman arsip.
           Monitor tidak boleh kosong saat demo (permintaan Andra 28 Okt 2026). */
        <CctvWall />
      ) : (() => {
        const isScreenMax = maximizedId === screenCam.id && !!screenStream;
        const maxKamera = isScreenMax ? screenCam : (maximizedId ? cameras.find(c => c.id === maximizedId) : null);
        const restKameras = maxKamera ? cameras.filter(c => c.id !== maximizedId) : cameras;
        const maxIndex = (maxKamera && !isScreenMax) ? cameras.findIndex(c => c.id === maximizedId) : 0;

        // Native browser fullscreen toggle — request/exit
        const toggleFullscreen = (cellEl) => {
          if (document.fullscreenElement) {
            document.exitFullscreen?.();
          } else if (cellEl?.requestFullscreen) {
            cellEl.requestFullscreen().catch(() => {});
          }
        };

        const handleEdit = (cam) => {
          // Pre-fill inline form untuk edit — reuse addingNew form
          setNewForm({
            nama: cam.nama || '',
            lokasi: cam.lokasi || '',
            stream_url: cam.stream_url || '',
          });
          setAddingNew(true);
        };
        const handleDelete = async (cam) => {
          const { data: deleted, error } = await supabase.from('kamera').delete().eq('id', cam.id).select();
          if (error) {
            alert('Gagal hapus: ' + error.message);
            return;
          }
          if (!deleted || deleted.length === 0) {
            alert(`KES Data memblokir DELETE kamera (RLS policy).\n\nHubungi admin untuk hapus manual.\nKamera ID: ${cam.id}`);
            return;
          }
          setCameras(prev => prev.filter(c => c.id !== cam.id));
          if (maximizedId === cam.id) setMaximizedId(null);
        };

        // YouTube Theater Mode (in-app):
        // - 1 kamera utama membesar di TOP (~65vh)
        // - Kamera lain jadi thumbnail strip di BAWAH (horizontal scroll)
        // - Klik thumbnail → swap jadi yang baru
        // - Klik tombol minimize / ESC → kembali ke grid
        if (maxKamera) {
          return (
            <div className="flex-1 overflow-y-auto c-scroll flex flex-col gap-3" style={{ minHeight: 0 }}>
              {/* MAIN STAGE — kamera maximized, full width */}
              <div className="shrink-0" style={{ height: '65vh', minHeight: 380 }}>
                <AutoScanPlayer
                  key={`max-${maxKamera.id}`}
                  kamera={maxKamera}
                  mediaStream={isScreenMax ? screenStream : undefined}
                  cameraNumber={maxIndex + 1}
                  embeddingsRef={embeddingsRef}
                  isMaximized={true}
                  onToggleMaximize={() => setMaximizedId(null)}
                  onToggleFullscreen={() => {
                    const el = document.querySelector('[data-cell-max]');
                    toggleFullscreen(el);
                  }}
                  onEdit={isScreenMax ? undefined : handleEdit}
                  onDelete={isScreenMax ? stopScreenCapture : handleDelete}
                  {...scanProps(maxKamera.id)}
                  scanInterval={1500}
                  onScanResult={onFaceDetected}
                />
              </div>

              {/* THUMBNAIL STRIP — kamera lain di BAWAH (horizontal grid) */}
              {restKameras.length > 0 && (
                <div className="shrink-0">
                  <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2"
                    style={{ color: 'var(--text-mute)' }}>
                    {t('nv_other_cameras')} ({restKameras.length})
                  </p>
                  <div className="grid gap-2"
                    style={{ gridTemplateColumns: `repeat(${Math.min(restKameras.length, 6)}, minmax(0, 1fr))` }}>
                    {restKameras.map((cam, i) => {
                      const camIdx = cameras.findIndex(c => c.id === cam.id);
                      return (
                        <div key={cam.id} className="cursor-pointer transition-transform hover:scale-[1.02]"
                          onClick={() => setMaximizedId(cam.id)}
                          title={`${t('nv_switch_to')} ${cam.nama}`}
                          style={{ aspectRatio: '16/10' }}>
                          <AutoScanPlayer
                            kamera={cam}
                            cameraNumber={camIdx + 1}
                            embeddingsRef={embeddingsRef}
                            isMaximized={false}
                            onToggleMaximize={() => setMaximizedId(cam.id)}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            scanEnabled={false}
                            scanInterval={6000}
                            scanDelayMs={i * 500}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        }

        // GRID MODE — default, semua kamera dalam grid uniform (scroll vertical)
        return (
          <div className="flex-1 overflow-y-auto c-scroll" style={{ minHeight: 0 }}>
            <div className="grid gap-3 pb-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {/* Jalur B — cell tangkapan tab/layar (YouTube) menempel di grid */}
              {screenStream && (
                <div key="screen-capture" style={{ aspectRatio: '16/10', minHeight: cols >= 3 ? 0 : 200 }}>
                  <AutoScanPlayer
                    kamera={screenCam}
                    mediaStream={screenStream}
                    cameraNumber={0}
                    embeddingsRef={embeddingsRef}
                    isMaximized={false}
                    onToggleMaximize={() => setMaximizedId(screenCam.id)}
                    onDelete={stopScreenCapture}
                    {...scanProps(screenCam.id)}
                    scanInterval={1500}
                    onScanResult={onFaceDetected}
                  />
                </div>
              )}
              {gridCams.map((cam, i) => (
                <div key={cam.id} style={{ aspectRatio: '16/10', minHeight: cols >= 3 ? 0 : 200 }}>
                  <AutoScanPlayer
                    kamera={cam}
                    cameraNumber={i + 1}
                    embeddingsRef={embeddingsRef}
                    isMaximized={false}
                    onToggleMaximize={() => setMaximizedId(cam.id)}
                    onToggleFullscreen={() => {
                      const el = document.querySelectorAll('[data-cell-grid]')[i];
                      toggleFullscreen(el);
                    }}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    {...scanProps(cam.id)}
                    scanInterval={3000}
                    onScanResult={onFaceDetected}
                  />
                </div>
              ))}
              {/* Sisa sel diisi CCTV publik (sumber sama dengan dinding CCTV), tanpa scan (#275). */}
              {fillTiles.map((tile, j) => (
                <div key={`pub-${tile.id}`} style={{ aspectRatio: '16/9', minHeight: cols >= 3 ? 0 : 200 }}>
                  <CctvCell cam={tile} index={gridCams.length + j} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {showBatch && (
        <BatchStreamModal cameras={cameras} onClose={() => setShowBatch(false)} onImported={() => {
          // Refresh from Supabase into shared state after batch import
          supabase.from('kamera').select('*').order('nama').then(({ data }) => {
            if (data) setCameras(data);
          });
          setShowBatch(false);
        }} />
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const typeIcon = { WAJAH_DIKENAL: Eye, WAJAH_TIDAK_DIKENAL: AlertTriangle, KENDARAAN: Car, OBJEK: Search };

// Feature 001 — Identification Log panel kanan
// Cap session-scoped 50 card terbaru, dedup by NIK, multi-camera = 1 card.
// Identified Card: clickable → buka Detail Popup (semua field personal).
// Unregistered Card: temporary, hilang saat wajah keluar frame, NOT clickable.
const MAX_IDENT_LOG = 50;
const RE_DETECTED_AUTO_CLEAR_MS = 5000;

const NeoVision = ({ kameras = [], setKameras = () => {}, hfStatus = 'waking', engineStatus = 'hf', setPage = () => {} }) => {
  const { t, lang } = useApp();
  // Default sub-tab: 'feed' (CCTV Monitor) — first thing user sees saat klik Vision
  const [source, setSource]           = useState('feed');
  const [scanning, setScanning]       = useState(false);
  const [detections, setDetections]   = useState([]);
  // sessionLog — akumulasi deteksi sepanjang sesi (cleared on refresh).
  // detections = transient (current scan saja, untuk bbox).
  // sessionLog = persistent dalam tab (untuk Log Aktivitas panel).
  // DB log_aktivitas = persistent di Supabase (cross-session).
  const [sessionLog, setSessionLog]   = useState([]);
  const SESSION_LOG_MAX = 100;
  const [selectedDet, setSelectedDet] = useState(null);
  // Vehicle popup — opens saat klik card kendaraan di panel detection.
  // Berisi data lengkap kendaraan (STNK + BPKB + pemilik + link ke Subject Profiling).
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  // Feature 001 state
  const [identLog, setIdentLog]       = useState([]);
  const [selectedIdent, setSelectedIdent] = useState(null);
  const identLogRef = useRef([]);
  // Sync ref dengan state untuk akses dari callback yang dijaga useCallback identity stable
  useEffect(() => { identLogRef.current = identLog; }, [identLog]);
  const [streamUrl, setStreamUrl]     = useState('');
  const [activeStreamUrl, setActiveStreamUrl] = useState(null);
  const [streamMaximized, setStreamMaximized] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [logs, setLogs]               = useState([]);
  const [hfError, setHfError]         = useState(null);
  const [autoScan, setAutoScan]       = useState(false);
  const [autoScanCount, setAutoScanCount] = useState(0);
  const [lastScanTs, setLastScanTs]   = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null); // { url, nama, kamera } for video-player mode
  const [searchQuery, setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [knownPersons, setKnownPersons] = useState([]);  // penduduk dengan face_embedding (siap offline)

  const canvasRef       = useRef(null);
  const overlayRef      = useRef(null); // real-time bbox overlay on live video
  const videoRef        = useRef(null);
  const streamRef       = useRef(null); // MediaStream — survives video element unmount
  const fileInputRef    = useRef(null);
  const imgRef          = useRef(null);
  const animRef         = useRef(null);
  const fileRef         = useRef(null);
  const autoScanRef     = useRef(null);
  const scanningRef     = useRef(false);
  const embeddingsRef   = useRef([]);        // [{nik, nama, intel, emb}] loaded from Supabase
  const lastDetRef      = useRef({ faces: [], objects: [] }); // latest InsightFace results for mesh loop
  const faceLandmarkerRef = useRef(null);    // MediaPipe FaceLandmarker instance
  const meshLoopRef     = useRef(null);      // requestAnimationFrame handle
  const faceQualityRef  = useRef(null);      // hasil analyzeFaceMesh frame terakhir (utk pre-filter & enrollment)
  const faceLandmarksRef = useRef([]);       // landmark 478 titik mentah frame terakhir (utk perekaman/capture)
  const blinkRef        = useRef(createBlinkDetector()); // liveness via kedip
  const meshSampleRef   = useRef(null);      // offscreen canvas kecil utk sampling oklusi
  const lastScanStartRef = useRef(0);        // jam mulai scan terakhir (pacing + pre-filter tanpa wajah)
  // Vote buffer: rata-rata embedding dari N frame berturutan (track via IoU bbox)
  const voteBufferRef   = useRef(createFaceVoteBuffer({ maxFrames: 5 }));
  const vehicleCacheRef = useRef({});        // { nik: ['B 1234 ABC', ...] } — avoid repeat fetches
  const pendudukCacheRef = useRef({});        // { nik: pendudukData } — avoid repeat Supabase queries

  const typeLabel = {
    WAJAH_DIKENAL:       t('jenis_known'),
    WAJAH_TIDAK_DIKENAL: t('jenis_unknown'),
    KENDARAAN:           t('jenis_vehicle'),
    OBJEK:               t('jenis_anomaly'),
  };

  const addLog = useCallback((msg, color = 'var(--text-dim)') => {
    setLogs(prev => {
      const next = [...prev.slice(-9), { time: nowTime(), msg, color }];
      // C-004: sync ke sessionStorage supaya DataCenter SystemLog tab bisa baca
      try { sessionStorage.setItem('horus_system_logs', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const runSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchLoading) return;
    setSearchLoading(true);
    const res = await searchPenduduk(searchQuery, 8);
    setSearchResults(res);
    setSearchLoading(false);
  }, [searchQuery, searchLoading]);

  // ── Feature 001 — DB error retry helper (SC-F13) ────────────────────────────
  // Saat lookup penduduk gagal (network blip / Supabase transient), JANGAN
  // langsung tandai unregistered. Retry sekali setelah 3 detik. Kalau tetap
  // gagal, baru fallback ke unregistered. Mencegah false-negative identifikasi.
  const lookupPendudukWithRetry = useCallback(async (nik) => {
    if (!nik) return null;
    try {
      const { data, error } = await supabase.from('penduduk').select('*').eq('nik', nik).maybeSingle();
      if (!error && data) return data;
      // Retry sekali setelah 3 detik
      await new Promise(r => setTimeout(r, 3000));
      const { data: data2 } = await supabase.from('penduduk').select('*').eq('nik', nik).maybeSingle();
      return data2 || null;
    } catch {
      return null;
    }
  }, []);

  // ── Session Log accumulator — append + dedup ke sessionLog dari current detections
  // Dedup key: nik (face) > no_plat (vehicle/plat) > type+label+bbox (objek)
  const mergeSessionLog = useCallback((dets) => {
    if (!Array.isArray(dets) || !dets.length) return;
    const keyOf = (d) =>
      d.nik
      || d.no_plat
      || d.plat
      || `${d.type}-${d.label || ''}-${(d.box ? Object.values(d.box).join(',') : (d.bbox || []).join(','))}`;
    setSessionLog(prev => {
      const map = new Map(prev.map(e => [keyOf(e), e]));
      for (const d of dets) {
        const k = keyOf(d);
        const existing = map.get(k);
        if (existing) {
          map.set(k, { ...existing, ...d, _firstSeen: existing._firstSeen, _lastSeen: Date.now(), _hits: (existing._hits || 1) + 1 });
        } else {
          map.set(k, { ...d, _firstSeen: Date.now(), _lastSeen: Date.now(), _hits: 1 });
        }
      }
      // Newest first, cap MAX
      const arr = Array.from(map.values()).sort((a,b) => (b._lastSeen||0) - (a._lastSeen||0));
      return arr.slice(0, SESSION_LOG_MAX);
    });
  }, []);

  // Auto-merge detections → sessionLog setiap kali detections updated
  useEffect(() => {
    if (detections.length > 0) mergeSessionLog(detections);
  }, [detections, mergeSessionLog]);

  // ── Feature 001 — Identification Log helpers ────────────────────────────────
  // addToIdentLog: dedup by NIK (multi-camera = 1 card), prepend baru di atas,
  // cap MAX_IDENT_LOG (hapus oldest IDENTIFIED card; unregistered tidak dihitung).
  // Saat re-detect orang yang sama → flag reDetected + reDetectedAt untuk badge.
  const addToIdentLog = useCallback((personData) => {
    setIdentLog(prev => {
      // Dedup: cari existing card dengan NIK yang sama (untuk identified)
      if (personData.type === 'identified' && personData.nik) {
        const existingIdx = prev.findIndex(p => p.nik && p.nik === personData.nik);
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = {
            ...updated[existingIdx],
            reDetected: true,
            reDetectedAt: Date.now(),
          };
          return updated;
        }
      }
      // Untuk unregistered: dedup by tempId (bbox-based)
      if (personData.type === 'unregistered' && personData.tempId) {
        const existingIdx = prev.findIndex(p => p.tempId && p.tempId === personData.tempId);
        if (existingIdx >= 0) return prev; // sudah ada, skip
      }
      const newLog = [{ ...personData, logId: Date.now() + Math.random() }, ...prev];
      // Cap: kalau melebihi MAX_IDENT_LOG, hapus oldest IDENTIFIED card
      if (newLog.length > MAX_IDENT_LOG) {
        const lastIdentIdx = newLog
          .map((p, i) => ({ p, i }))
          .filter(({ p }) => p.type === 'identified')
          .pop()?.i;
        if (lastIdentIdx !== undefined) newLog.splice(lastIdentIdx, 1);
      }
      return newLog;
    });
  }, []);

  // removeUnregistered: hapus unregistered card by tempId saat wajah hilang
  // Identified card TIDAK dihapus (tetap di log meski orang keluar frame).
  const removeUnregistered = useCallback((tempIds) => {
    if (!tempIds?.length) return;
    setIdentLog(prev => prev.filter(p =>
      p.type !== 'unregistered' || !tempIds.includes(p.tempId)
    ));
  }, []);

  // Re-detected badge auto-clear setelah 5000ms
  useEffect(() => {
    const timer = setInterval(() => {
      setIdentLog(prev => {
        const hasReDetected = prev.some(p => p.reDetected);
        if (!hasReDetected) return prev;
        return prev.map(p =>
          p.reDetected && Date.now() - (p.reDetectedAt || 0) > RE_DETECTED_AUTO_CLEAR_MS
            ? { ...p, reDetected: false }
            : p
        );
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ESC handler untuk close Detail Popup
  useEffect(() => {
    if (!selectedIdent) return;
    const handler = (e) => { if (e.key === 'Escape') setSelectedIdent(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIdent]);

  // ── Supabase embedding loader ───────────────────────────────────────────────

  const loadEmbeddings = useCallback(async () => {
    // Fetch SEMUA penduduk yang punya minimal 1 jenis embedding (512-dim ATAU 128-dim).
    // 512-dim (face_embedding) = ArcFace HF Space — engine utama
    // 128-dim (face_embedding_light) = FaceNet face-api.js — engine fallback browser-side
    const { data, error } = await supabase.from('penduduk')
      .select('nik, nama_lengkap, status_intelijen, foto_url, face_embedding, face_embedding_light')
      .or('face_embedding.not.is.null,face_embedding_light.not.is.null')
      .order('nama_lengkap');
    if (error) {
      console.warn('[loadEmbeddings] fail:', error.message);
      addLog(lang === 'id' ? `Gagal muat embedding: ${error.message}` : `Failed to load embedding: ${error.message}`, '#ff2d55');
      embeddingsRef.current = [];
      return;
    }
    embeddingsRef.current = (data || []).map(p => ({
      nik:        p.nik,
      nama:       p.nama_lengkap,
      intel:      p.status_intelijen,
      emb:        p.face_embedding,         // 512-dim ArcFace
      emb_light:  p.face_embedding_light,   // 128-dim FaceNet fallback
    }));
    setKnownPersons((data || []).map(p => ({
      nik:    p.nik,
      nama:   p.nama_lengkap,
      intel:  p.status_intelijen,
      foto:   p.foto_url,
      hasHF:  !!p.face_embedding,
      hasFB:  !!p.face_embedding_light,
    })));
    // Tampilkan total semua entitas dari Supabase (orang, wajah, kendaraan, plat)
    try {
      const [{ count: orang },{ count: kend }] = await Promise.all([
        supabase.from('penduduk').select('*', { count: 'exact', head: true }),
        supabase.from('kendaraan').select('*', { count: 'exact', head: true }),
      ]);
      const wajah = embeddingsRef.current.length;
      const dbMsg = lang === 'en'
        ? `Database: ${orang ?? '?'} citizens · ${wajah} faces · ${kend ?? '?'} vehicles/plates loaded`
        : `Basis Data: ${orang ?? '?'} penduduk · ${wajah} wajah · ${kend ?? '?'} kendaraan/plat dimuat`;
      addLog(dbMsg, 'var(--color-green)');
    } catch {
      const fallbackMsg = lang === 'en'
        ? `${embeddingsRef.current.length} faces loaded from database`
        : `${embeddingsRef.current.length} wajah dimuat dari database`;
      addLog(fallbackMsg, 'var(--color-green)');
    }
  }, [addLog]);

  // Eagerly load embeddings on mount + realtime listener + 5min safety refresh.
  // Stream tab + Camera tab share embeddingsRef, jadi harus auto-update tiap kali
  // foto wajah baru ditambahkan/dihapus di Datacenter atau Intel.
  useEffect(() => {
    loadEmbeddings();
    const channel = supabase
      .channel('neovision-penduduk-embedding')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'penduduk' }, () => loadEmbeddings())
      .subscribe();
    const interval = setInterval(loadEmbeddings, 5 * 60 * 1000); // 300000ms safety net
    return () => {
      clearInterval(interval);
      try { channel.unsubscribe(); } catch { /* noop */ }
      supabase.removeChannel(channel);
    };
  }, [loadEmbeddings]);

  // Per-NIK dedup state — module-level ALERT_DEDUP_MS controls window
  const lastAlertRef = useRef({});  // { nik: timestampMs }

  const getVehiclesForNik = useCallback(async (nik) => {
    if (vehicleCacheRef.current[nik] !== undefined) return vehicleCacheRef.current[nik];
    const { data } = await supabase.from('kendaraan').select('no_plat,merk_model,warna').eq('nik_pemilik', nik);
    const plates = (data || []).map(v => v.no_plat);
    vehicleCacheRef.current[nik] = plates;
    return plates;
  }, []);

  const getPendudukForNik = useCallback(async (nik) => {
    const cached = pendudukCacheRef.current[nik];
    if (cached !== undefined) return cached; // null means "not found", still valid cache
    const { data, error } = await supabase.from('penduduk')
      .select('nik, nama_lengkap, tempat_lahir, tanggal_lahir, jenis_kelamin, gol_darah, agama, status_perkawinan, pekerjaan, kewarganegaraan, alamat_lengkap, rt_rw, kel_desa, kecamatan, kota_kabupaten, status_intelijen, catatan_fisik, foto_url, last_seen, lokasi_last, confidence_last, dokumen_ktp_url, dokumen_passport_url, dokumen_akta_url, dokumen_kk_url')
      .eq('nik', nik).maybeSingle();   // 0 baris = data null tanpa galat (single() melempar 406 tiap kali)
    // Galat jaringan/izin (mis. token belum terpasang) JANGAN disimpan sebagai "tidak ada": nama tak akan pulih sampai muat ulang.
    if (error) { console.warn('getPendudukForNik error:', error.message); return null; }
    pendudukCacheRef.current[nik] = data ?? null;
    return data ?? null;
  }, []);

  // ── MediaPipe Face Mesh ─────────────────────────────────────────────────────

  const initFaceMesh = useCallback(async () => {
    if (faceLandmarkerRef.current) return;
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
      );
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
          delegate: 'CPU',
        },
        runningMode: 'VIDEO',
        numFaces: 5,
        outputFaceBlendshapes: false,
      });
      addLog('KESMesh siap (478 titik landmark)', '#7c4dff');
    } catch (e) {
      console.warn('MediaPipe Face Mesh tidak tersedia:', e.message);
    }
  }, [addLog]);

  // Draw InsightFace boxes on a ctx (without clearing)
  const drawBoxesOnCtx = useCallback((ctx, faces, objects, w, h) => {
    const drawCorners = (x1, y1, x2, y2, color) => {
      const cl = Math.min(16, (x2 - x1) * 0.2);
      ctx.strokeStyle = color; ctx.lineWidth = 2.5;
      ctx.beginPath();
      [[x1,y1+cl,x1,y1,x1+cl,y1],[x2-cl,y1,x2,y1,x2,y1+cl],
       [x2,y2-cl,x2,y2,x2-cl,y2],[x1+cl,y2,x1,y2,x1,y2-cl]]
        .forEach(([ax,ay,bx,by,cx,cy]) => { ctx.moveTo(ax,ay); ctx.lineTo(bx,by); ctx.lineTo(cx,cy); });
      ctx.stroke();
    };

    for (const face of faces) {
      const [x1, y1, x2, y2] = face.bbox;
      const isKnown  = face.status === 'KNOWN';
      const isWanted = face._intel === 'WANTED';
      const isSuspect = face._intel === 'SUSPECT';
      const boxColor = isWanted ? '#ff2d55' : isSuspect ? '#ff6b2b' : isKnown ? '#00d4ff' : '#ff2d55';
      const conf = Math.round((face.match_score ?? face.confidence ?? 0) * 100);
      drawCorners(x1, y1, x2, y2, boxColor);

      if (isKnown) {
        const cardX = Math.max(0, x1);
        const cardW = Math.max(x2 - x1, 240);
        const cardH = 80;
        const cardY = Math.min(y2 + 4, h - cardH - 2);
        ctx.fillStyle = isWanted ? 'rgba(255,45,85,0.93)' : isSuspect ? 'rgba(255,107,43,0.93)' : 'rgba(0,20,40,0.93)';
        ctx.beginPath(); ctx.roundRect(cardX, cardY, cardW, cardH, 5); ctx.fill();
        // Accent top bar
        ctx.fillStyle = isWanted ? '#ff2d55' : isSuspect ? '#ff6b2b' : '#00d4ff';
        ctx.beginPath(); ctx.roundRect(cardX, cardY, cardW, 3, [5, 5, 0, 0]); ctx.fill();

        const textColor = isWanted || isSuspect ? '#02060e' : '#e2f4ff';
        const dimColor  = isWanted || isSuspect ? '#1a0008' : '#7ab8d4';
        ctx.fillStyle = textColor;
        ctx.font = 'bold 12px "IBM Plex Mono", monospace';
        ctx.fillText((face._nama || 'DIKENAL').substring(0, 26), cardX + 8, cardY + 19);

        ctx.fillStyle = dimColor;
        ctx.font = '8.5px "IBM Plex Mono", monospace';
        ctx.fillText(`NIK: ${maskNik(face.match_nik)}`, cardX + 8, cardY + 32);

        const ttlShort = face._ttl ? face._ttl.substring(0, 32) : '—';
        ctx.fillText(`TTL: ${ttlShort}`, cardX + 8, cardY + 44);

        ctx.fillStyle = textColor;
        ctx.font = 'bold 9px "IBM Plex Mono", monospace';
        const umurLabel = face._umur != null ? `UMUR: ${face._umur} TAHUN` : '';
        ctx.fillText(umurLabel, cardX + 8, cardY + 57);

        ctx.font = '8.5px "IBM Plex Mono", monospace';
        ctx.fillStyle = isWanted ? '#ff0' : isSuspect ? '#ffd60a' : '#00ff88';
        const gShort = face._gender === 'LAKI-LAKI' ? 'L' : face._gender === 'PEREMPUAN' ? 'P' : (face.gender || '');
        ctx.fillText(`${face._intel || 'CLEAR'} · ${gShort} · ${conf}%`, cardX + 8, cardY + 70);
      } else {
        ctx.font = 'bold 11px "IBM Plex Mono", monospace';
        const text = `TIDAK DIKENAL · ${conf}%`;
        const tw = ctx.measureText(text).width + 10;
        const lx = x1, ly = y1 > 22 ? y1 - 22 : y2;
        ctx.fillStyle = 'rgba(255,45,85,0.90)';
        ctx.beginPath(); ctx.roundRect(lx, ly, tw, 18, 4); ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(text, lx + 5, ly + 13);
      }
    }

    for (const obj of objects) {
      const [x1, y1, x2, y2] = obj.bbox;
      const isWeapon = AI_WEAPON_LABELS.has((obj.label || '').toLowerCase());
      const oColor = isWeapon ? '#ff2d55' : '#ffd60a';
      drawCorners(x1, y1, x2, y2, oColor);
      ctx.font = 'bold 11px "IBM Plex Mono", monospace';
      const text = isWeapon
        ? `SENJATA ${(obj.label || '').toUpperCase()} ${Math.round(obj.confidence*100)}%`
        : `${obj.label} ${Math.round(obj.confidence*100)}%`;
      const tw = ctx.measureText(text).width + 10;
      const ly = y1 > 22 ? y1 - 22 : y2;
      ctx.fillStyle = oColor;
      ctx.beginPath(); ctx.roundRect(x1, ly, tw, 18, 4); ctx.fill();
      ctx.fillStyle = isWeapon ? '#ffffff' : '#02060e';
      ctx.fillText(text, x1 + 5, ly + 13);
    }
  }, []);

  // Face mesh animation loop — runs continuously when camera is active
  const startMeshLoop = useCallback(() => {
    cancelAnimationFrame(meshLoopRef.current);
    const loop = async () => {
      const video = videoRef.current;
      const cvs   = overlayRef.current;
      const fl    = faceLandmarkerRef.current;
      if (!video || !cvs || video.paused) {
        meshLoopRef.current = requestAnimationFrame(loop);
        return;
      }
      const w = video.videoWidth, h = video.videoHeight;
      if (!w || !h || video.readyState < 2) {
        meshLoopRef.current = requestAnimationFrame(loop);
        return;
      }
      if (cvs.width !== w) cvs.width = w;
      if (cvs.height !== h) cvs.height = h;
      const ctx = cvs.getContext('2d');
      ctx.clearRect(0, 0, w, h);

      // Layer 1: MediaPipe face mesh (478 landmarks) + analitik nyata
      if (fl) {
        try {
          const results = fl.detectForVideo(video, performance.now());
          const allFaces = results.faceLandmarks || [];

          // Sampler oklusi: gambar video ke canvas kecil 1x per frame, baca piksel.
          let sampler = null;
          if (allFaces.length) {
            try {
              let sc = meshSampleRef.current;
              if (!sc) { sc = document.createElement('canvas'); meshSampleRef.current = sc; }
              const sw = 160, sh = Math.max(1, Math.round(160 * h / w));
              if (sc.width !== sw) sc.width = sw;
              if (sc.height !== sh) sc.height = sh;
              const sctx = sc.getContext('2d', { willReadFrequently: true });
              sctx.drawImage(video, 0, 0, sw, sh);
              sampler = makeLumaVarianceSampler(sctx.getImageData(0, 0, sw, sh), sw, sh);
            } catch { sampler = null; }
          } else {
            faceQualityRef.current = null;   // tidak ada wajah
          }

          let primaryQ = null;
          for (let fi = 0; fi < allFaces.length; fi++) {
            const landmarks = allFaces[fi];
            const q = analyzeFaceMesh(landmarks, { sampler });
            if (fi === 0) primaryQ = q;

            // Warna mesh mengikuti kualitas: hijau bagus, kuning sedang, merah jelek.
            const score = q?.qualityScore ?? 0;
            const mainColor = score >= 75 ? '0,255,136' : score >= 50 ? '255,214,10' : '255,45,85';

            // Mesh wajah 478 titik 3D real-time (MediaPipe FaceLandmarker).
            drawFaceMeshLayers(ctx, landmarks, w, h, mainColor);
          }

          // Simpan landmark mentah frame ini supaya bisa ikut direkam saat capture.
          faceLandmarksRef.current = allFaces;

          // Simpan metrik wajah utama + update liveness (kedip), lalu gambar HUD.
          if (primaryQ) {
            const live = blinkRef.current.update(primaryQ.ear, performance.now());
            primaryQ.liveness = live;
            faceQualityRef.current = primaryQ;
            drawMeshHud(ctx, primaryQ, live, w, h);
          }
        } catch { /* ignore per-frame errors */ }
      }

      // Layer 2: InsightFace detection boxes (updated every 1.5s by auto-scan)
      const { faces, objects } = lastDetRef.current;
      if (faces.length || objects.length) {
        drawBoxesOnCtx(ctx, faces, objects, w, h);
      }

      meshLoopRef.current = requestAnimationFrame(loop);
    };
    meshLoopRef.current = requestAnimationFrame(loop);
  }, [drawBoxesOnCtx]);

  // ── Batch scan state ────────────────────────────────────────────────────────
  const [batchQueue, setBatchQueue]     = useState([]); // {id, file, thumbUrl, status, result}
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchDrag, setBatchDrag]       = useState(false);
  const batchInputRef = useRef();

  const addBatchFiles = (files) => {
    const arr = Array.from(files).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (!arr.length) return;
    setBatchQueue(prev => [
      ...prev,
      ...arr.map(f => ({
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        file: f,
        thumbUrl: URL.createObjectURL(f),
        isVideo: f.type.startsWith('video/'),
        status: 'waiting',
        result: null,
      })),
    ]);
  };

  // Extract first frame from a video file → returns a JPEG File for AI scan
  const extractVideoFrame = (videoFile, timeS = 0.1) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let settled = false;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } };
    video.onloadeddata = () => { try { video.currentTime = timeS; } catch (e) { settled = true; cleanup(); reject(e); } };
    video.onseeked = () => {
      if (settled) return;
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 360;
      const cap = document.createElement('canvas');
      cap.width = w; cap.height = h;
      cap.getContext('2d').drawImage(video, 0, 0, w, h);
      cap.toBlob(blob => {
        settled = true;
        cleanup();
        if (!blob) return reject(new Error('frame blob fail'));
        resolve(new File(
          [blob],
          videoFile.name.replace(/\.[^.]+$/, '') + '_frame.jpg',
          { type: 'image/jpeg' }
        ));
      }, 'image/jpeg', 0.9);
    };
    video.onerror = () => { if (!settled) { settled = true; cleanup(); reject(new Error('video load fail')); } };
  });

  // Helper: load File → Image element + size
  const loadImageEl = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ img, w: img.naturalWidth, h: img.naturalHeight, url }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });

  // Build composite image with bbox overlay → return objectURL (for thumb)
  const buildCompositeUrl = async (file, scanResult) => {
    const { img, w, h, url } = await loadImageEl(file);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const overlay = document.createElement('canvas');
    renderAllBboxes(overlay, w, h, scanResult);
    ctx.drawImage(overlay, 0, 0);
    URL.revokeObjectURL(url);
    return new Promise(resolve => canvas.toBlob(b => resolve(URL.createObjectURL(b)), 'image/jpeg', 0.85));
  };

  const runBatchScan = useCallback(async () => {
    if (batchRunning) return;
    setBatchRunning(true);
    setBatchQueue(prev => prev.map(q => q.status === 'waiting' ? { ...q, status: 'queued' } : q));

    // Video items have their own AutoScanPlayer running — skip them in batch processing
    const snapshot = batchQueue.filter(q => !q.isVideo && (q.status === 'waiting' || q.status === 'queued'));
    for (const item of snapshot) {
      setBatchQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'scanning' } : q));
      addLog(`Scan: ${item.file.name}`, 'var(--color-cyan)');
      try {
        const fileToScan = item.file;
        // Decode file ke Image untuk Tier-2 OCR crop (F0-A7)
        const sourceImg = await new Promise((res, rej) => {
          const img = new Image();
          img.onload = () => res(img);
          img.onerror = rej;
          img.src = URL.createObjectURL(fileToScan);
        }).catch(() => null);
        const rawData  = await callHFAnalyze(fileToScan, 60000);
        const result   = await enrichScanResult(rawData, embeddingsRef, sourceImg);
        const known    = result.enrichedFaces.filter(f => f._person);
        const unknown  = result.enrichedFaces.filter(f => !f._person);

        // Feature 001 — push ke Identification Log (dedup by NIK / tempId)
        for (const f of result.enrichedFaces) {
          const p = f._person;
          const mandatoryOk = p && p.nama_lengkap && p.nik && p.foto_url;
          if (mandatoryOk) {
            addToIdentLog({
              type: 'identified',
              nik: p.nik,
              nama: p.nama_lengkap,
              foto: p.foto_url,
              status: p.status_intelijen || 'CLEAR',
              penduduk: p,
              reDetected: false,
              reDetectedAt: null,
              firstSeenAt: Date.now(),
            });
          } else {
            // Tidak ada match atau missing mandatory field → unregistered (temporary)
            addToIdentLog({
              type: 'unregistered',
              tempId: (f.bbox || []).join('-') + '-' + Date.now(),
              firstSeenAt: Date.now(),
            });
          }
        }

        // Build full KTP person list (sama seperti dets di Upload File)
        const personDetails = known.map(f => {
          const p = f._person;
          const plates = (f._vehicles || []).map(v => `${v.no_plat} (${v.merk_model || '-'})`);
          return {
            nik: p.nik,
            nama: p.nama_lengkap,
            status: p.status_intelijen,
            tempat_lahir: p.tempat_lahir,
            tanggal_lahir: p.tanggal_lahir,
            jenis_kelamin: p.jenis_kelamin,
            kota: p.kota_kabupaten,
            alamat: p.alamat_lengkap,
            pekerjaan: p.pekerjaan,
            foto_url: p.foto_url,
            plates,
            confidence: Math.round((f.match_score ?? f.confidence ?? 0) * 100),
          };
        });
        const threats = personDetails.filter(d => d.status === 'WANTED' || d.status === 'SUSPECT');

        // Plate ownership info per detected plate (kendaraan + pemilik)
        const plateDetails = result.enrichedPlates.map(p => ({
          plat: p.plate,
          merk: p.info?.merk_model || null,
          warna: p.info?.warna || null,
          pemilik: p.info?.nama_pemilik || null,
          intel: p.info?.intel || null,
          stnk_aktif: p.info?.masa_berlaku_stnk ? new Date(p.info.masa_berlaku_stnk) >= new Date() : null,
        }));

        // Auto-alert
        const adhocKamera = { id: `batch-${item.id.slice(-6)}`, nama: 'Batch Scan', lokasi: item.file.name };
        const alertCount = await dispatchAlerts(result, adhocKamera);
        logScanResult(result, adhocKamera);
        if (alertCount > 0) addLog(`⚠ ${alertCount} alert: ${threats.map(t => t.nama).join(', ')}`, '#ff2d55');

        // Build composite thumbnail showing bbox overlay
        // Video: pakai frame jpg yang sudah di-extract (image File). Foto: pakai file asli.
        let overlayThumbUrl = item.thumbUrl;
        try { overlayThumbUrl = await buildCompositeUrl(fileToScan, result); }
        catch (e) { console.warn('[batch composite] fail:', e?.message); }

        setBatchQueue(prev => prev.map(q => q.id === item.id ? {
          ...q, status: 'done',
          thumbUrl: overlayThumbUrl,
          result: {
            known: known.length, unknown: unknown.length,
            vehicles: result.vehicleObjs.length,
            plates: result.enrichedPlates,
            personDetails, // full KTP per matched face
            plateDetails,  // full kendaraan + pemilik per plat
            threats,
          },
        } : q));
      } catch (err) {
        addLog(`Error: ${item.file.name} — ${err.message}`, '#ff2d55');
        setBatchQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error' } : q));
      }
    }
    setBatchRunning(false);
    addLog(`Batch selesai — ${snapshot.length} foto diproses`, 'var(--color-green)');
  }, [batchRunning, batchQueue, addLog, embeddingsRef]);

  const clearBatch = () => {
    batchQueue.forEach(q => { try { URL.revokeObjectURL(q.thumbUrl); } catch { /* ok */ } });
    setBatchQueue([]);
  };

  // ── Canvas draw ─────────────────────────────────────────────────────────────

  const drawDetections = useCallback((dets) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width: W, height: H } = canvas;
    if (imgRef.current) ctx.drawImage(imgRef.current, 0, 0, W, H);
    // scanline overlay
    ctx.fillStyle = 'rgba(0,212,255,0.02)';
    for (let y = 0; y < H; y += 4) ctx.fillRect(0, y, W, 1);
    dets.forEach(det => {
      const { x, y, w, h } = det.box;
      const px = x * W, py = y * H, pw = w * W, ph = h * H;
      const c = det.color;
      ctx.fillStyle = c + '12';
      ctx.fillRect(px, py, pw, ph);
      ctx.strokeStyle = c;
      ctx.lineWidth = 2;
      ctx.strokeRect(px, py, pw, ph);
      const cs = 12;
      ctx.lineWidth = 3;
      [[px,py,cs,0,0,cs],[px+pw,py,-cs,0,0,cs],[px,py+ph,cs,0,0,-cs],[px+pw,py+ph,-cs,0,0,-cs]]
        .forEach(([ox,oy,dx1,dy1,dx2,dy2]) => {
          ctx.beginPath(); ctx.moveTo(ox+dx1,oy+dy1); ctx.lineTo(ox,oy); ctx.lineTo(ox+dx2,oy+dy2); ctx.stroke();
        });
      const labelH = 22;
      ctx.fillStyle = c + 'ee';
      ctx.fillRect(px, py - labelH, pw, labelH);
      ctx.fillStyle = det.type === 'WAJAH_DIKENAL' ? '#02060e' : '#ffffff';
      ctx.font = `bold 10px "IBM Plex Mono", monospace`;
      ctx.fillText(`${det.label}  ${det.conf}%`, px + 6, py - 7);
    });
  }, []);

  // ── Map HF response → detections + auto-alert ───────────────────────────────

  const processHFResult = useCallback(async (data, imgW, imgH) => {
    const dets = [];

    addLog(`DETEKSI — ${data.objects.length} objek terdeteksi`);
    addLog(`WAJAH — ${data.faces.length} wajah diproses`);

    // Kumpulkan plat per bbox asal (untuk label kendaraan)
    const plateMap = {};
    data.plates.forEach(p => {
      const key = p.from_bbox?.join(',');
      if (key) plateMap[key] = p.text;
    });

    // Kendaraan
    data.objects.forEach(obj => {
      if (!AI_VEHICLE_LABELS.has(obj.label)) return;
      const box = bboxToRel(obj.bbox, imgW, imgH);
      const plateKey = obj.bbox.join(',');
      const plate = plateMap[plateKey];
      dets.push({
        id:    `obj-${obj.bbox.join('-')}`,
        type:  'KENDARAAN',
        label: plate || obj.label.toUpperCase(),
        sub:   plate ? `${obj.label} · plat terdeteksi` : obj.label,
        conf:  Math.round(obj.confidence * 100),
        color: '#ff6b2b',
        box,
      });
      if (plate) {
        addLog(`Plat ${plate} terbaca`, '#ff6b2b');
      }
    });

    // HANYA orang + kendaraan — drop semua objek lain (chair, table, dll)
    // Sistem HORUS scope: identifikasi orang (KTP) + kendaraan (TNKB+STNK+pemilik).
    // Loop dibekukan dengan filter false — kept untuk struktur kode.
    data.objects
      .filter(() => false)
      .forEach(obj => {
        const box = bboxToRel(obj.bbox, imgW, imgH);
        dets.push({
          id:    `obj-${obj.bbox.join('-')}`,
          type:  'OBJEK',
          label: obj.label,
          sub:   `conf ${Math.round(obj.confidence * 100)}%`,
          conf:  Math.round(obj.confidence * 100),
          color: '#ffd60a',
          box,
        });
      });

    // Wajah — fetch penduduk untuk KNOWN faces
    for (const face of data.faces) {
      const box  = bboxToRel(face.bbox, imgW, imgH);
      const conf = Math.round(face.match_score * 100);

      if (face.status === 'KNOWN' && face.match_nik) {
        const p = await getPendudukForNik(face.match_nik);

        const nama       = p?.nama_lengkap    || face.match_nik;
        const intel      = p?.status_intelijen || 'UNKNOWN';
        const gShort     = p?.jenis_kelamin === 'LAKI-LAKI' ? 'L' : p?.jenis_kelamin === 'PEREMPUAN' ? 'P' : (face.gender || '');
        const umur       = calcAge(p?.tanggal_lahir); // exact from DB only
        const ttl        = fmtTTL(p?.tempat_lahir, p?.tanggal_lahir);
        addLog(`Match: ${nama} (${conf}%)`, 'var(--color-cyan)');

        const plates  = await getVehiclesForNik(face.match_nik);
        const platStr = plates.length ? ` · ${plates.join(', ')}` : '';

        dets.push({
          id:    `face-${face.bbox.join('-')}`,
          type:  'WAJAH_DIKENAL',
          label: nama,
          sub:   `${intel} · ${gShort}${umur != null ? ' · '+umur+'th' : ''} · ${p?.kota_kabupaten || ''}${platStr}`,
          conf,
          color:       'var(--color-cyan)',
          box,
          nik:         face.match_nik,
          intel,
          umur,
          ttl,
          kota:        p?.kota_kabupaten  || '',
          kecamatan:       p?.kecamatan          || '',
          kel_desa:        p?.kel_desa           || '',
          rt_rw:           p?.rt_rw              || '',
          alamat:          p?.alamat_lengkap     || '',
          gender:          p?.jenis_kelamin      || '',
          genderShort:     gShort,
          pekerjaan:       p?.pekerjaan          || '',
          gol_darah:       p?.gol_darah          || '',
          agama:           p?.agama              || '',
          status_kawin:    p?.status_perkawinan  || '',
          kewarganegaraan: p?.kewarganegaraan    || '',
          catatan:         p?.catatan_fisik      || '',
          last_seen:       p?.last_seen          || '',
          lokasi_last:     p?.lokasi_last        || '',
          foto_url:        p?.foto_url           || '',
        });

        if (intel === 'WANTED' || intel === 'SUSPECT') {
          addLog(`⚠ ${intel} — mengirim alert`, '#ff2d55');
          await supabase.from('alerts').insert({
            jenis:         'WAJAH_DIKENAL',
            subjek:        nama,
            lokasi:        'NeoVision — Pemindaian',
            waktu:         nowTime(),
            tanggal:       nowDate(),
            confidence:    conf,
            tindakan:      `Wajah ${intel} terdeteksi. Tindak lanjut segera.`,
            status_subjek: intel,
            resolved:      false,
          });
        }
      } else {
        addLog(lang === 'id' ? `Wajah tidak dikenal — skor ${conf}%` : `Unknown face — score ${conf}%`, '#ff2d55');
        dets.push({
          id:    `face-${face.bbox.join('-')}`,
          type:  'WAJAH_TIDAK_DIKENAL',
          label: lang === 'id' ? 'Tidak Dikenal' : 'Unknown',
          sub:   `${face.gender} · ±${face.age} tahun · Alert disimpan`,
          conf:  Math.round(face.confidence * 100),
          color: '#ff2d55',
          box,
        });
      }
    }

    if (data.plates.length > 0 && data.objects.filter(o => AI_VEHICLE_LABELS.has(o.label)).length === 0) {
      data.plates.forEach(p => {
        addLog(`Plat: ${p.text} (${Math.round(p.confidence * 100)}%)`, '#ff6b2b');
      });
    }

    return dets;
  }, [addLog, getPendudukForNik, getVehiclesForNik]);

  // ── Scan via shared aiScanner pipeline (rich bbox + DB lookup) ──────────────

  const runScan = useCallback(async (img, file) => {
    imgRef.current  = img;
    fileRef.current = file;
    const w = img.naturalWidth  || img.width  || 640;
    const h = img.naturalHeight || img.height || 480;

    setScanning(true); setScanProgress(0); setDetections([]); setSelectedDet(null);
    setImageLoaded(true); setLogs([]); setHfError(null);
    addLog('Memulai pemindaian...');

    // Wait one frame so canvasRef is populated by React
    await new Promise(r => requestAnimationFrame(r));
    const canvas = canvasRef.current;
    if (!canvas) { setScanning(false); return; }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Animated scan-line until HF returns
    let prog = 0;
    if (animRef.current) clearInterval(animRef.current);
    animRef.current = setInterval(() => {
      prog = Math.min(95, prog + 3);
      setScanProgress(prog);
      ctx.drawImage(img, 0, 0, w, h);
      const y = (prog / 100) * h;
      const grad = ctx.createLinearGradient(0, y - 30, 0, y + 4);
      grad.addColorStop(0, 'rgba(0,212,255,0)');
      grad.addColorStop(1, 'rgba(0,212,255,0.5)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, y - 30, w, 34);
      ctx.fillStyle = 'rgba(0,212,255,0.9)';
      ctx.fillRect(0, y, w, 2);
    }, 30);

    let scanResult = null;
    try {
      const rawData = await callHFAnalyze(file, 60000);
      // Pass img element untuk Tier-2 OCR plate crop (F0-A7)
      scanResult = await enrichScanResult(rawData, embeddingsRef, img);
      addLog(lang === 'id'
        ? `Wajah: ${scanResult.enrichedFaces.length} · Kendaraan: ${scanResult.vehicleObjs.length} · Plat: ${scanResult.enrichedPlates.length}`
        : `Faces: ${scanResult.enrichedFaces.length} · Vehicles: ${scanResult.vehicleObjs.length} · Plates: ${scanResult.enrichedPlates.length}`, 'var(--color-green)');
      // Auto-alert WANTED/SUSPECT detections (Upload File scan acts like a CCTV snapshot)
      const adhocKamera = { id: 'upload-file', nama: 'Upload File', lokasi: 'Pemindaian Manual' };
      const alertCount = await dispatchAlerts(scanResult, adhocKamera);
      logScanResult(scanResult, adhocKamera);
      if (alertCount > 0) addLog(`⚠ ${alertCount} alert dibuat`, '#ff2d55');
    } catch (err) {
      setHfError(err.message);
      addLog(`Error: ${err.message}`, '#ff2d55');
    }

    clearInterval(animRef.current);
    animRef.current = null;
    setScanProgress(100);
    setScanning(false);

    // Final render: image + rich bbox overlay on the same canvas
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    if (scanResult) {
      // Final render: image + rich bbox overlay on the same canvas
      const overlay = document.createElement('canvas');
      renderAllBboxes(overlay, w, h, scanResult);
      ctx.drawImage(overlay, 0, 0);
      setDetections(buildDetsFromScan(scanResult));

      // Populate Subjek Terdeteksi (identLog) — dedup by NIK across uploads
      for (const face of scanResult.enrichedFaces) {
        const p = face._person;
        if (p && p.nama_lengkap && p.nik) {
          addToIdentLog({
            type: 'identified',
            nik: p.nik,
            nama: p.nama_lengkap,
            foto: p.foto_url,
            status: p.status_intelijen || 'CLEAR',
            penduduk: p,  // full row untuk Detail popup
            firstSeenAt: Date.now(),
          });
        } else if (face.bbox) {
          addToIdentLog({
            type: 'unregistered',
            tempId: `unreg-upload-${face.bbox.join('-')}`,
            firstSeenAt: Date.now(),
          });
        }
      }
    }
  }, [addLog, embeddingsRef, addToIdentLog]);

  // Convert an enriched scan result into the rich `dets` array consumed by
  // the right-side detection panel. Used by Upload File one-shot scan AND
  // by the AutoScanPlayer onScanResult callback for video mode.
  const buildDetsFromScan = useCallback((scanResult) => {
    const { enrichedFaces, enrichedPlates, vehicleObjs, otherObjs } = scanResult;
    const dets = [];
    for (const f of enrichedFaces) {
      const conf = Math.round((f.match_score ?? f.confidence ?? 0) * 100);
      const p = f._person;
      if (p) {
        let umur = null;
        if (p.tanggal_lahir) {
          const birth = new Date(p.tanggal_lahir);
          const today = new Date();
          umur = today.getFullYear() - birth.getFullYear();
          if (today.getMonth() < birth.getMonth() ||
             (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) umur--;
        }
        const ttl = [p.tempat_lahir, p.tanggal_lahir
          ? new Date(p.tanggal_lahir).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
          : null].filter(Boolean).join(', ');
        const plates = (f._vehicles || []).map(v => v.no_plat);
        const platStr = plates.length ? ` · ${plates.slice(0, 2).join(', ')}` : '';
        dets.push({
          id: `face-${f.bbox.join('-')}`,
          type: 'WAJAH_DIKENAL',
          label: p.nama_lengkap || f.match_nik || 'DIKENAL',
          sub: `${p.status_intelijen || 'CLEAR'} · ${(p.jenis_kelamin === 'LAKI-LAKI' ? 'L' : p.jenis_kelamin === 'PEREMPUAN' ? 'P' : '—')}${umur != null ? ' · '+umur+'th' : ''} · ${p.kota_kabupaten || ''}${platStr}`,
          conf,
          color: (p.status_intelijen === 'WANTED' || p.status_intelijen === 'SUSPECT') ? '#ff2d55' : 'var(--color-cyan)',
          nik: p.nik,
          intel: p.status_intelijen,
          ttl, umur,
          gender: p.jenis_kelamin,
          genderShort: p.jenis_kelamin === 'LAKI-LAKI' ? 'L' : p.jenis_kelamin === 'PEREMPUAN' ? 'P' : '',
          tanggal_lahir: p.tanggal_lahir,
          tempat_lahir: p.tempat_lahir,
          gol_darah: p.gol_darah,
          agama: p.agama,
          status_kawin: p.status_perkawinan,
          pekerjaan: p.pekerjaan,
          kewarganegaraan: p.kewarganegaraan,
          alamat: p.alamat_lengkap,
          rt_rw: p.rt_rw,
          kel_desa: p.kel_desa,
          kecamatan: p.kecamatan,
          kota: p.kota_kabupaten,
          catatan: p.catatan_fisik,
          last_seen: p.last_seen,
          lokasi_last: p.lokasi_last,
          foto_url: p.foto_url,
          vehicles: f._vehicles || [],
        });
      } else {
        dets.push({
          id: `face-unk-${f.bbox.join('-')}`,
          type: 'WAJAH_TIDAK_DIKENAL', label: lang === 'id' ? 'Tidak Dikenal' : 'Unknown',
          sub: `${f.gender === 'L' ? 'L' : f.gender === 'P' ? 'P' : '—'} · ±${f.age || '?'} th`,
          conf, color: '#ff2d55',
        });
      }
    }
    for (const obj of vehicleObjs) {
      const key = obj.bbox.join(',');
      const p = enrichedPlates.find(pp => pp.from_bbox?.join(',') === key);
      const v = p?.info;
      dets.push({
        id: `veh-${obj.bbox.join('-')}`,
        type: 'KENDARAAN',
        label: p?.plate || obj.label.toUpperCase(),
        sub: v ? `${v.merk_model || ''} ${v.warna || ''}${v.nama_pemilik ? ' · '+v.nama_pemilik : ''}`.trim() : `${obj.label} · belum terdaftar`,
        conf: Math.round(obj.confidence * 100),
        color: (v?.intel === 'WANTED' || v?.intel === 'SUSPECT') ? '#ff2d55' : '#ff6b2b',
        plat: p?.plate, intel: v?.intel,
        // Carry FULL kendaraan info untuk dipakai oleh VehiclePopup (klik card).
        // v punya: no_plat, merk_model, warna, tipe_kendaraan, tahun, no_rangka,
        // no_mesin, masa_berlaku_stnk, no_registrasi, tgl_registrasi, status_stnk,
        // tnkb_url, bpkb_url, foto_url, nama_pemilik, pemilik (full KTP).
        vehicle: v || null,
        bbox: obj.bbox,
      });
    }
    for (const obj of otherObjs) {
      dets.push({
        id: `obj-${obj.bbox.join('-')}`,
        type: 'OBJEK', label: obj.label, sub: `conf ${Math.round(obj.confidence*100)}%`,
        conf: Math.round(obj.confidence*100), color: '#ffd60a',
      });
    }
    return dets;
  }, []);

  // Callback from AutoScanPlayer (video mode) — push detections to right panel
  const handleVideoScanResult = useCallback((scanResult) => {
    setDetections(buildDetsFromScan(scanResult));
  }, [buildDetsFromScan]);

  // Feature 001 — Live Feed scan results → Identification Log + Session Log
  // (1) Faces → identLog (Subject Profiling-style identity card)
  // (2) Detections (faces + vehicles) → sessionLog (DETECTION RESULTS panel kanan)
  const handleFeedScanResult = useCallback((scanResult) => {
    const { enrichedFaces } = scanResult;

    // (1) identLog — orang
    for (const face of enrichedFaces) {
      const p = face._person;
      if (p) {
        const parts = (p.nama_lengkap || '').trim().split(/\s+/);
        const hasAll = parts.length >= 2 && parts[0] && parts[1] && p.nik && p.foto_url;
        if (hasAll) {
          addToIdentLog({
            type: 'identified',
            nik: p.nik,
            nama: p.nama_lengkap,
            foto: p.foto_url,
            status: p.status_intelijen || 'CLEAR',
            penduduk: p,
            firstSeenAt: Date.now(),
          });
        } else {
          addToIdentLog({ type: 'unregistered', tempId: `unk-${(face.bbox||[]).join('-')}`, firstSeenAt: Date.now() });
        }
      } else {
        addToIdentLog({ type: 'unregistered', tempId: `unk-${(face.bbox||[]).join('-')}`, firstSeenAt: Date.now() });
      }
    }

    // (2) sessionLog — semua deteksi (orang + kendaraan) untuk panel ACTIVITY LOG.
    // mergeSessionLog dedup by NIK / no_plat / bbox key, jadi multi-cell + multi-scan
    // tidak duplicate. Detections dibuild via buildDetsFromScan yang sama dengan
    // upload mode → output cards konsisten antara CCTV Monitor & Upload Files.
    const dets = buildDetsFromScan(scanResult);
    if (dets.length) mergeSessionLog(dets);
  }, [addToIdentLog, buildDetsFromScan, mergeSessionLog]);

  // Extract first frame from a video File → returns { img: HTMLImageElement, jpgFile: File }
  const extractFirstFrame = (videoFile) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    let settled = false;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } };
    const fail = (err) => { if (settled) return; settled = true; cleanup(); reject(err); };
    video.onloadeddata = () => { try { video.currentTime = 0.1; } catch (e) { fail(e); } };
    video.onseeked = () => {
      if (settled) return;
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 360;
      const cap = document.createElement('canvas');
      cap.width = w; cap.height = h;
      cap.getContext('2d').drawImage(video, 0, 0, w, h);
      cap.toBlob((blob) => {
        if (!blob) return fail(new Error('frame blob fail'));
        const jpgFile = new File(
          [blob],
          videoFile.name.replace(/\.[^.]+$/, '') + '_frame.jpg',
          { type: 'image/jpeg' }
        );
        const img = new Image();
        const frameUrl = URL.createObjectURL(blob);
        img.onload = () => {
          if (settled) { URL.revokeObjectURL(frameUrl); return; }
          settled = true;
          cleanup();
          resolve({ img, jpgFile, _frameUrl: frameUrl });
        };
        img.onerror = () => fail(new Error('frame image load fail'));
        img.src = frameUrl;
      }, 'image/jpeg', 0.9);
    };
    video.onerror = () => fail(new Error('video load fail (codec tidak didukung browser?)'));
  });

  // Unified handler — foto: scan once on canvas. Video: render as AutoScanPlayer (live).
  const handleSingleFile = async (file) => {
    if (!file) return;
    const isVideo = (file.type || '').startsWith('video/');
    if (isVideo) {
      // Cleanup previous uploaded video URL if any
      if (uploadedVideo?.url) { try { URL.revokeObjectURL(uploadedVideo.url); } catch { /* ignore */ } }
      const url = URL.createObjectURL(file);
      const adhocKamera = {
        id: 'upload-video-' + Date.now(),
        nama: file.name,
        lokasi: 'Upload File · Video',
        status: 'ONLINE',
        playlist: [{ url, nama: file.name, kind: 'video', size: file.size, uploaded_at: new Date().toISOString() }],
      };
      setUploadedVideo({ url, nama: file.name, kamera: adhocKamera });
      setImageLoaded(false); setDetections([]); setHfError(null); setLogs([]);
      addLog(`Video diterima — player aktif, scan AI tiap 3 detik`, '#7c4dff');
    } else {
      // Reset video player if active
      if (uploadedVideo?.url) { try { URL.revokeObjectURL(uploadedVideo.url); } catch { /* ignore */ } setUploadedVideo(null); }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { runScan(img, file); URL.revokeObjectURL(url); };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    }
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (files.length > 1) { addBatchFiles(files); e.target.value = ''; return; }
    handleSingleFile(files[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    if (files.length > 1) { addBatchFiles(files); return; }
    handleSingleFile(files[0]);
  };

  const activateCamera = async () => {
    try {
      // Minta resolusi setinggi mungkin (ideal 1080p, boleh sampai 4K kalau kamera
      // mendukung). Wajah jauh (>2 m) jadi punya lebih banyak piksel → mesh & OCR
      // tetap terbaca lebih jauh. Browser otomatis turun ke resolusi terdekat.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width:  { ideal: 1920, max: 3840 },
          height: { ideal: 1080, max: 2160 },
          facingMode: 'environment',
        },
      });
      // Stop any previous stream before replacing (defensive)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      } else {
        // Component unmounted while permission dialog was open — release tracks
        stream.getTracks().forEach(tk => tk.stop());
        streamRef.current = null;
      }
    } catch { alert(t('cam_error')); }
  };

  const stopCamera = () => {
    setAutoScan(false);
    clearInterval(autoScanRef.current);
    cancelAnimationFrame(meshLoopRef.current);
    lastDetRef.current = { faces: [], objects: [] };
    lastAlertRef.current = {}; // reset per-NIK dedup so next session can re-alert immediately
    voteBufferRef.current?.clear(); // bersihkan track wajah antar sesi
    // Primary path: streamRef survives even if videoRef unmounted (tab switch / route change)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(tk => tk.stop());
      streamRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      try { videoRef.current.srcObject.getTracks?.().forEach(tk => tk.stop()); } catch { /* already stopped */ }
      videoRef.current.srcObject = null;
    }
    setCameraActive(false); setImageLoaded(false); setDetections([]);
    setAutoScanCount(0); setLastScanTs(null);
    const cvs = overlayRef.current;
    if (cvs) cvs.getContext('2d').clearRect(0, 0, cvs.width, cvs.height);
  };

  // Centralized tab switch — auto-stops camera when leaving Camera tab,
  // releases uploaded-video object URL when leaving Upload File tab
  const handleTabChange = (newSource) => {
    if (source === 'camera' && newSource !== 'camera') stopCamera();
    if (source === 'upload' && newSource !== 'upload' && uploadedVideo?.url) {
      try { URL.revokeObjectURL(uploadedVideo.url); } catch { /* ignore */ }
      setUploadedVideo(null);
    }
    if (newSource !== 'batch') { setImageLoaded(false); setDetections([]); }
    setSource(newSource);
  };

  // Draw real-time detection boxes on overlay canvas
  const drawOverlay = useCallback((faces, objects, vidW, vidH) => {
    const cvs = overlayRef.current;
    if (!cvs) return;
    cvs.width  = vidW;
    cvs.height = vidH;
    const ctx = cvs.getContext('2d');
    ctx.clearRect(0, 0, vidW, vidH);

    const drawBox = (x1, y1, x2, y2, color, label, sub) => {
      const cl = Math.min(16, (x2 - x1) * 0.2);
      // Corner brackets saja, tanpa isian (permintaan Andra 19 Okt)
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      [
        [x1, y1+cl, x1, y1, x1+cl, y1],
        [x2-cl, y1, x2, y1, x2, y1+cl],
        [x2, y2-cl, x2, y2, x2-cl, y2],
        [x1+cl, y2, x1, y2, x1, y2-cl],
      ].forEach(([ax,ay,bx,by,cx,cy]) => {
        ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(cx, cy);
      });
      ctx.stroke();
      // Label pill
      if (label) {
        const text = sub ? `${label}  ${sub}` : label;
        ctx.font = 'bold 11px IBM Plex Mono, monospace';
        const tw = ctx.measureText(text).width + 10;
        const lx = x1, ly = y1 > 20 ? y1 - 20 : y2;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(lx, ly, tw, 18, 4);
        ctx.fill();
        ctx.fillStyle = '#02060e';
        ctx.fillText(text, lx + 5, ly + 13);
      }
    };

    for (const face of faces) {
      const [x1, y1, x2, y2] = face.bbox;
      const isKnown = face.status === 'KNOWN';
      const color   = isKnown ? 'var(--color-cyan)' : '#ff2d55';
      const conf    = Math.round((face.match_score ?? face.confidence ?? 0) * 100);
      const label   = isKnown ? (face._nama || face.match_nik || 'DIKENAL') : 'UNKNOWN';
      const intel   = face._intel || '';
      const badgeColor = intel === 'WANTED' ? '#ff2d55' : intel === 'SUSPECT' ? '#ff6b2b' : color;
      drawBox(x1, y1, x2, y2, badgeColor, label, `${conf}%${intel ? ' · '+intel : ''}`);
    }
    for (const obj of objects) {
      const [x1, y1, x2, y2] = obj.bbox;
      const isWeapon = AI_WEAPON_LABELS.has((obj.label || '').toLowerCase());
      drawBox(x1, y1, x2, y2, isWeapon ? '#ff2d55' : '#ffd60a',
        isWeapon ? `SENJATA ${(obj.label || '').toUpperCase()}` : obj.label,
        `${Math.round(obj.confidence*100)}%`);
    }
  }, []);

  // Real-time CCTV auto-scan: capture → HF → client-side match → mesh loop picks up results
  const runAutoScanFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video || scanningRef.current) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    if (!w || !h) return;

    // Pre-filter kecepatan: kalau mesh aktif dan TIDAK ada wajah sama sekali,
    // lewati scan ini supaya tidak buang panggilan server. Tetap paksa kirim
    // tiap SCAN_NOFACE_FORCE_MS agar kendaraan / wajah yang luput mesh tetap tertangkap.
    // Fail-open: kalau mesh belum siap, jalan normal seperti biasa.
    const nowMs0 = Date.now();
    const sinceLast = nowMs0 - lastScanStartRef.current;
    if (sinceLast < SCAN_MIN_PERIOD_MS) return;
    if (faceLandmarkerRef.current && !faceQualityRef.current && sinceLast < SCAN_NOFACE_FORCE_MS) return;
    lastScanStartRef.current = nowMs0;

    // Claim the lock BEFORE the async toBlob to prevent overlapping scans
    scanningRef.current = true;
    setScanning(true);

    const cap = document.createElement('canvas');
    const sc = Math.min(1, SCAN_MAX_SIDE / Math.max(w, h));
    cap.width = Math.round(w * sc); cap.height = Math.round(h * sc);
    cap.getContext('2d').drawImage(video, 0, 0, cap.width, cap.height);

    cap.toBlob(async blob => {
      if (!blob) {
        scanningRef.current = false;
        setScanning(false);
        return;
      }
      const ts = nowTime();

      try {
        // Blob polos (bukan File): callHFAnalyze memakainya apa adanya, tanpa decode + encode ulang (~50 ms).
        const data = await callHFAnalyze(blob, 60000);
        // bbox dari server ada di ruang piksel frame terkirim; overlay & mesh memakai ukuran video asli.
        if (sc < 1) for (const o of [...(data.faces || []), ...(data.objects || [])]) if (o.bbox) o.bbox = o.bbox.map(v => Math.round(v / sc));

        // Multi-frame vote: rata-rata embedding dari N frame wajah yang sama
        // (di-track via IoU overlap bbox). Vektor rata-rata lebih stabil dan
        // meningkatkan akurasi pencocokkan saat wajah bergerak atau sedikit miring.
        if (data.faces?.length && voteBufferRef.current) {
          const nowMs = Date.now();
          for (const face of data.faces) {
            if (face.bbox && Array.isArray(face.embedding) && face.embedding.length) {
              const vote = voteBufferRef.current.update(face.bbox, face.embedding, nowMs);
              face.embedding = vote.embedding;
              face._voteFrames = vote.frames;
            }
          }
        }

        // Client-side face matching against Supabase embeddings (persistent, ignores HF face_db)
        const enrichedFaces = [];
        for (const face of data.faces || []) {
          let enriched = { ...face };

          // Adaptive threshold: lebih rendah saat embedding sudah dirata-rata >=3 frame
          const voteFrames = face._voteFrames || 1;
          const adaptThreshold = voteFrames >= 3 ? FACE_MATCH_THRESHOLD - 0.03 : FACE_MATCH_THRESHOLD;

          // Prefer client-side match (uses embedding stored in Supabase)
          const { nik: csNik, sim: csSim, person } = clientMatch(face.embedding, embeddingsRef.current);
          if (csNik && csSim >= adaptThreshold) {
            enriched.status      = 'KNOWN';
            enriched.match_nik   = csNik;
            enriched.match_score = csSim;
            enriched._nama       = person.nama;
            enriched._intel      = person.intel;
            const p = await getPendudukForNik(csNik);
            if (p) {
              enriched._nama            = p.nama_lengkap    || person.nama;
              enriched._intel           = p.status_intelijen || person.intel;
              enriched._gender          = p.jenis_kelamin   || '';
              enriched._kota            = p.kota_kabupaten  || '';
              enriched._kecamatan       = p.kecamatan       || '';
              enriched._kel_desa        = p.kel_desa        || '';
              enriched._rt_rw           = p.rt_rw           || '';
              enriched._alamat          = p.alamat_lengkap  || '';
              enriched._pekerjaan       = p.pekerjaan       || '';
              enriched._gol_darah       = p.gol_darah       || '';
              enriched._agama           = p.agama           || '';
              enriched._status_kawin    = p.status_perkawinan || '';
              enriched._kewarganegaraan = p.kewarganegaraan || '';
              enriched._catatan         = p.catatan_fisik   || '';
              enriched._last_seen       = p.last_seen       || '';
              enriched._lokasi_last     = p.lokasi_last     || '';
              enriched._ttl             = fmtTTL(p.tempat_lahir, p.tanggal_lahir);
              enriched._umur            = calcAge(p.tanggal_lahir);
              enriched._foto_url        = p.foto_url        || '';
            }
          } else if (face.status === 'KNOWN' && face.match_nik) {
            enriched._nama  = face.match_nik;
            enriched._intel = '';
            const p = await getPendudukForNik(face.match_nik);
            if (p) {
              enriched._nama            = p.nama_lengkap       || face.match_nik;
              enriched._intel           = p.status_intelijen   || '';
              enriched._gender          = p.jenis_kelamin      || '';
              enriched._kota            = p.kota_kabupaten     || '';
              enriched._kecamatan       = p.kecamatan          || '';
              enriched._kel_desa        = p.kel_desa           || '';
              enriched._rt_rw           = p.rt_rw              || '';
              enriched._alamat          = p.alamat_lengkap     || '';
              enriched._pekerjaan       = p.pekerjaan          || '';
              enriched._gol_darah       = p.gol_darah          || '';
              enriched._agama           = p.agama              || '';
              enriched._status_kawin    = p.status_perkawinan  || '';
              enriched._kewarganegaraan = p.kewarganegaraan    || '';
              enriched._catatan         = p.catatan_fisik      || '';
              enriched._last_seen       = p.last_seen          || '';
              enriched._lokasi_last     = p.lokasi_last        || '';
              enriched._ttl             = fmtTTL(p.tempat_lahir, p.tanggal_lahir);
              enriched._umur            = calcAge(p.tanggal_lahir);
              enriched._foto_url        = p.foto_url           || '';
            }
          }

          if (enriched.status === 'KNOWN' && (enriched._intel === 'WANTED' || enriched._intel === 'SUSPECT')) {
            const dedupKey = enriched.match_nik || enriched._nama;
            const last = lastAlertRef.current[dedupKey] || 0;
            const nowMs = Date.now();
            if (nowMs - last >= ALERT_DEDUP_MS) {
              lastAlertRef.current[dedupKey] = nowMs;
              addLog(`⚡ ${enriched._intel}: ${enriched._nama}`, '#ff2d55');
              const { error } = await supabase.from('alerts').insert({
                jenis: 'WAJAH_DIKENAL', subjek: enriched._nama,
                lokasi: 'Kamera Perangkat — Live CCTV',
                waktu: ts, tanggal: nowDate(),
                confidence: Math.round((enriched.match_score ?? 0) * 100),
                tindakan: `${enriched._intel} terdeteksi real-time.`,
                status_subjek: enriched._intel, resolved: false,
              });
              if (error) console.warn('[alert insert] fail:', error.message);
            }
          }
          enrichedFaces.push(enriched);
        }

        // Update lastDetRef — mesh loop will draw boxes on top of face mesh.
        // Scope: HANYA kendaraan + senjata yang digambar (objek lain dibuang).
        lastDetRef.current = {
          faces: enrichedFaces,
          objects: (data.objects || []).filter(o =>
            AI_VEHICLE_LABELS.has(o.label) || AI_WEAPON_LABELS.has((o.label || '').toLowerCase())),
        };

        // Log ke Supabase log_aktivitas (dedup window per subjek)
        logScanResult(
          {
            enrichedFaces,
            enrichedPlates: [],
            vehicleObjs: (data.objects || []).filter(o => AI_VEHICLE_LABELS.has(o.label)),
            otherObjs: [],
            weaponObjs: (data.objects || []).filter(o => AI_WEAPON_LABELS.has((o.label || '').toLowerCase())),
          },
          { id: 'kamera-perangkat', nama: 'Kamera Perangkat', lokasi: 'Live CCTV' }
        );

        // Populate Subjek Terdeteksi (identLog) — dedup by NIK across frames.
        // Fetch full penduduk row supaya Detail popup bisa render semua field KTP.
        for (const face of enrichedFaces) {
          if (face.status === 'KNOWN' && face.match_nik) {
            const p = await getPendudukForNik(face.match_nik);
            addToIdentLog({
              type: 'identified',
              nik: face.match_nik,
              nama: p?.nama_lengkap || face._nama || face.match_nik,
              status: p?.status_intelijen || face._intel || 'CLEAR',
              foto: p?.foto_url || face._foto_url || null,
              penduduk: p || null,  // ← raw row untuk Detail popup data-personal grid
            });
          } else if (face.bbox) {
            const tempId = `unreg-${(face.bbox || []).join('-')}`;
            addToIdentLog({ type: 'unregistered', tempId });
          }
        }

        // Update detection panel
        const dets = [];
        for (const face of enrichedFaces) {
          const conf = Math.round((face.match_score ?? face.confidence ?? 0) * 100);
          let platStr = '';
          if (face.status === 'KNOWN' && face.match_nik) {
            const plates = await getVehiclesForNik(face.match_nik);
            if (plates.length) platStr = ` · ${plates.join(', ')}`;
          }
          const gShort = face._gender === 'LAKI-LAKI' ? 'L' : face._gender === 'PEREMPUAN' ? 'P' : (face.gender || '');
          const umur = face._umur ?? null; // exact from DB only
          dets.push({
            id: `face-${(face.bbox || []).join('-')}-${face.match_nik || 'x'}`,
            type: face.status === 'KNOWN' ? 'WAJAH_DIKENAL' : 'WAJAH_TIDAK_DIKENAL',
            label: face._nama || (face.status === 'KNOWN' ? face.match_nik : (lang === 'id' ? 'Tidak Dikenal' : 'Unknown')),
            sub: face.status === 'KNOWN'
              ? `${face._intel || 'CLEAR'} · ${gShort}${umur != null ? ' · '+umur+'th' : ''} · ${face._kota || ''}${platStr}`
              : `${conf}%`,
            conf, color: face.status === 'KNOWN' ? 'var(--color-cyan)' : '#ff2d55', box: null,
            nik:          face.match_nik || null,
            intel:        face._intel || '',
            umur,
            ttl:          face._ttl || '',
            kota:             face._kota            || '',
            kecamatan:        face._kecamatan        || '',
            kel_desa:         face._kel_desa         || '',
            rt_rw:            face._rt_rw            || '',
            alamat:           face._alamat           || '',
            gender:           face._gender           || face.gender || '',
            genderShort:      gShort,
            pekerjaan:        face._pekerjaan        || '',
            gol_darah:        face._gol_darah        || '',
            agama:            face._agama            || '',
            status_kawin:     face._status_kawin     || '',
            kewarganegaraan:  face._kewarganegaraan  || '',
            catatan:          face._catatan          || '',
            last_seen:        face._last_seen        || '',
            lokasi_last:      face._lokasi_last      || '',
            foto_url:         face._foto_url         || '',
          });
        }
        // HANYA orang + kendaraan — sistem HORUS tidak baca objek lain (chair, table, dll).
        // Person sudah jadi WAJAH_*. Vehicle sudah jadi KENDARAAN. OBJEK type di-skip.
        setDetections(dets); // reflect current frame, even when empty
        setAutoScanCount(c => c + 1);
        setLastScanTs(ts);
      } catch (e) {
        addLog(`CCTV error: ${e.message}`, '#ff6b2b');
        lastScanStartRef.current = Date.now() + 1400; // galat: ulangi 2 s lagi, bukan tiap 600 ms
      } finally {
        scanningRef.current = false;
        setScanning(false);
      }
    }, 'image/jpeg', 0.75);
  }, [addLog, getPendudukForNik, getVehiclesForNik]);

  useEffect(() => {
    if (autoScan && cameraActive) {
      // One press activates everything: embeddings, face mesh, bbox loop, auto scan
      loadEmbeddings();
      startMeshLoop();
      initFaceMesh();
      runAutoScanFrame();
      if (autoScanRef.current) clearInterval(autoScanRef.current);
      autoScanRef.current = setInterval(runAutoScanFrame, SCAN_TICK_MS);
    } else {
      clearInterval(autoScanRef.current);
      autoScanRef.current = null;
      cancelAnimationFrame(meshLoopRef.current);
      lastDetRef.current = { faces: [], objects: [] };
      if (overlayRef.current) {
        const cvs = overlayRef.current;
        cvs.getContext('2d').clearRect(0, 0, cvs.width, cvs.height);
      }
    }
    return () => {
      clearInterval(autoScanRef.current);
      autoScanRef.current = null;
    };
  }, [autoScan, cameraActive, runAutoScanFrame, loadEmbeddings, startMeshLoop, initFaceMesh]);

  // Capture frame from camera and scan.
  // Penting: frame BERSIH dikirim ke AI engine (recognition), sedangkan gambar
  // yang DIREKAM/ditampilkan menyertakan mesh 478 titik supaya titik-titiknya
  // benar-benar ikut terekam (bukan hanya tampil sekilas di layar).
  const captureAndScan = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth, h = video.videoHeight;

    // 1) Frame bersih → file untuk AI engine (jangan kotori dengan overlay).
    const clean = document.createElement('canvas');
    clean.width = w; clean.height = h;
    clean.getContext('2d').drawImage(video, 0, 0, w, h);

    // 2) Komposit: frame + mesh 478 titik → gambar yang direkam/ditampilkan.
    const comp = document.createElement('canvas');
    comp.width = w; comp.height = h;
    const cctx = comp.getContext('2d');
    cctx.drawImage(video, 0, 0, w, h);
    const lms = faceLandmarksRef.current || [];
    for (const landmarks of lms) drawFaceMeshLayers(cctx, landmarks, w, h, '0,212,255');

    clean.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      const img = new Image();
      img.onload = () => runScan(img, file);
      img.src = comp.toDataURL('image/jpeg', 0.9);   // gambar rekaman = ada mesh
    }, 'image/jpeg', 0.9);
  };

  useEffect(() => () => {
    clearInterval(animRef.current);
    clearInterval(autoScanRef.current);
    cancelAnimationFrame(meshLoopRef.current);
    try { faceLandmarkerRef.current?.close?.(); } catch { /* ignore */ }
    faceLandmarkerRef.current = null;
    batchQueue.forEach(q => { try { URL.revokeObjectURL(q.thumbUrl); } catch { /* ignore */ } });
    stopCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab order: CCTV Monitor / Kamera Perangkat / Upload Device (i18n).
  // Upload Device: drop 1 file = single scan, drop banyak file = batch grid otomatis.
  const tabs = [
    { id: 'feed',   label: t('tab_feed'),   icon: Tv },
    { id: 'camera', label: t('tab_camera'), icon: Camera },
    { id: 'upload', label: t('tab_upload'), icon: Upload },
  ];

  return (
    <div className={`h-full flex flex-col gap-3 p-4${source === 'feed' ? ' overflow-hidden' : ' overflow-y-auto c-scroll'}`} style={{ background: 'var(--color-bg)' }}>

      <div className="flex gap-2 shrink-0">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => handleTabChange(id)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all"
            style={{ background: source === id ? '#00d4ff18' : 'var(--color-card)', border: `1px solid ${source === id ? '#00d4ff44' : 'var(--border-lo)'}`, color: source === id ? 'var(--color-cyan)' : 'var(--text-dim)' }}>
            <Icon size={14} />{label}
          </button>
        ))}
        {/* AI status badge dihilangkan — status sudah tampil di Header (status DB+AI) */}
      </div>

      {/* Live Feed branch handled di dalam shared layout below */}

      {(
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0" style={{ minHeight: source === 'feed' ? 0 : 420 }}>

        {/* Left: preview / canvas */}
        <div className="flex-1 flex flex-col gap-3 min-w-0">
          {source === 'feed' && (
            <FeedTab cameras={kameras} setCameras={setKameras} onFaceDetected={handleFeedScanResult} />
          )}
          {source === 'upload' && (
            <>
              {/* Canvas is ALWAYS mounted so canvasRef is valid before first scan; we just toggle visibility */}
              <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileUpload} />

              {/* Drop zone — visible when nothing loaded (single OR batch) */}
              {!imageLoaded && !uploadedVideo && batchQueue.length === 0 && (
                <div className="flex-1 rounded-2xl flex flex-col items-center justify-center gap-4 cursor-pointer transition-all"
                  style={{ border: '2px dashed var(--border-mid)', background: 'var(--color-card)', minHeight: 320 }}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
                  <Upload size={40} color="rgba(0,212,255,0.3)" strokeWidth={1.5} />
                  <div className="text-center">
                    <p className="text-sm font-bold" style={{ color: 'var(--text-dim)' }}>{t('drop_multi')}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-mute)' }}>{t('drop_multi_hint')}</p>
                  </div>
                </div>
              )}

              {/* Video mode — render AutoScanPlayer with auto-scan running */}
              {uploadedVideo && (
                <div className="flex-1 flex flex-col gap-2" style={{ minHeight: 320 }}>
                  <AutoScanPlayer
                    kamera={uploadedVideo.kamera}
                    embeddingsRef={embeddingsRef}
                    scanInterval={3000}
                    scanEnabled={true}
                    onScanResult={handleVideoScanResult}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => { try { URL.revokeObjectURL(uploadedVideo.url); } catch { /* ignore */ } setUploadedVideo(null); }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black"
                      style={{ background: 'rgba(255,45,85,0.15)', border: '1px solid rgba(255,45,85,0.3)', color: '#ff2d55' }}>
                      <X size={11} /> {t('close_video')}
                    </button>
                    <button onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black"
                      style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
                      <Upload size={11} /> {t('upload_other')}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex-1 relative rounded-2xl overflow-hidden"
                style={{ background: '#000', minHeight: 320, display: imageLoaded ? 'block' : 'none' }}>
                <canvas ref={canvasRef} className="w-full h-full object-contain" style={{ display: 'block' }} />
                  {scanning && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-xl"
                      style={{ background: 'rgba(2,6,14,0.9)', border: '1px solid rgba(0,212,255,0.3)' }}>
                      <div className="w-1.5 h-1.5 rounded-full animate-blink" style={{ background: 'var(--color-cyan)' }} />
                      <span className="text-[9px] font-black font-mono" style={{ color: 'var(--color-cyan)' }}>{t('scanning')} {scanProgress}%</span>
                    </div>
                  )}
                  {hfError && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-xl"
                      style={{ background: 'rgba(2,6,14,0.9)', border: '1px solid rgba(255,45,85,0.3)' }}>
                      <span className="text-[9px] font-black font-mono" style={{ color: '#ff2d55' }}>⚠ {hfError}</span>
                    </div>
                  )}
                  <button onClick={() => { setImageLoaded(false); setDetections([]); setHfError(null); }}
                    className="absolute top-3 right-3 flex items-center justify-center rounded-xl"
                    style={{ width: 30, height: 30, background: 'rgba(2,6,14,0.9)', border: '1px solid rgba(255,45,85,0.3)' }}>
                    <X size={12} color="#ff2d55" />
                  </button>
              </div>
            </>
          )}

          {source === 'upload' && batchQueue.length > 0 && (
            <div className="flex-1 flex flex-col gap-3 min-w-0" style={{ minHeight: 320 }}>
              {/* Toolbar */}
              <div className="flex items-center gap-2 shrink-0">
                <div
                  onClick={() => batchInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setBatchDrag(true); }}
                  onDragLeave={() => setBatchDrag(false)}
                  onDrop={e => { e.preventDefault(); setBatchDrag(false); addBatchFiles(e.dataTransfer.files); }}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl cursor-pointer transition-all"
                  style={{ height: 44, border: `2px dashed ${batchDrag ? 'var(--color-cyan)' : 'var(--border-mid)'}`, background: batchDrag ? 'rgba(0,212,255,0.05)' : 'var(--color-card)' }}>
                  <Upload size={13} color={batchDrag ? 'var(--color-cyan)' : 'var(--text-dim)'} />
                  <span className="text-xs font-bold" style={{ color: batchDrag ? 'var(--color-cyan)' : 'var(--text-dim)' }}>
                    Seret foto atau video atau klik untuk tambah
                  </span>
                </div>
                <input ref={batchInputRef} type="file" accept="image/*,video/*" multiple className="hidden"
                  onChange={e => { addBatchFiles(e.target.files); e.target.value = ''; }} />
                {batchQueue.length > 0 && !batchRunning && (
                  <button onClick={runBatchScan}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black shrink-0"
                    style={{ background: 'var(--color-cyan)', color: '#02060e' }}>
                    <Zap size={13} /> Scan Foto ({batchQueue.filter(q => !q.isVideo && q.status === 'waiting').length})
                  </button>
                )}
                {batchRunning && (
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black shrink-0"
                    style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
                    <div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} />
                    Memindai...
                  </div>
                )}
                {batchQueue.length > 0 && !batchRunning && (
                  <button onClick={clearBatch}
                    className="flex items-center justify-center rounded-xl shrink-0"
                    style={{ width: 36, height: 36, background: 'rgba(255,45,85,0.1)', border: '1px solid rgba(255,45,85,0.25)' }}>
                    <X size={12} color="#ff2d55" />
                  </button>
                )}
              </div>

              {/* Summary bar when done */}
              {batchQueue.length > 0 && batchQueue.every(q => q.status === 'done' || q.status === 'error') && (
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl shrink-0"
                  style={{ background: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.2)' }}>
                  <CheckCircle size={12} color="var(--color-green)" />
                  <span className="text-[9px] font-black" style={{ color: 'var(--color-green)' }}>
                    Selesai: {batchQueue.filter(q => q.status === 'done').length} berhasil · {batchQueue.filter(q => q.status === 'error').length} gagal
                  </span>
                  {batchQueue.some(q => q.result?.threats?.some(th => th.status === 'WANTED' || th.status === 'SUSPECT')) && (
                    <span className="ml-auto text-[9px] font-black px-2 py-0.5 rounded-lg animate-pulse"
                      style={{ background: 'rgba(255,45,85,0.2)', border: '1px solid rgba(255,45,85,0.4)', color: '#ff2d55' }}>
                      ⚠ ALERT TERDETEKSI
                    </span>
                  )}
                </div>
              )}

              {/* Empty state */}
              {batchQueue.length === 0 && (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 rounded-2xl"
                  style={{ background: 'var(--color-card)', border: '1px solid var(--border-lo)' }}>
                  <Layers size={44} color="rgba(0,212,255,0.15)" strokeWidth={1} />
                  <div className="text-center">
                    <p className="text-sm font-bold" style={{ color: 'var(--text-dim)' }}>{t('batch_empty_title')}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-mute)' }}>{t('batch_empty_hint')}</p>
                  </div>
                </div>
              )}

              {/* Grid of results */}
              {batchQueue.length > 0 && (
                <div className="flex-1 overflow-y-auto c-scroll">
                  <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                    {batchQueue.map((item, idx) => {
                      // Video item → live player with auto-scan (AutoScanPlayer handles its own bbox)
                      if (item.isVideo) {
                        const adhocKamera = {
                          id: `batch-vid-${item.id.slice(-8)}`,
                          nama: item.file.name.length > 28 ? item.file.name.slice(0, 28) + '…' : item.file.name,
                          lokasi: 'Batch Scan',
                          status: 'ONLINE',
                          playlist: [{ url: item.thumbUrl, nama: item.file.name, kind: 'video', size: item.file.size }],
                        };
                        return (
                          <div key={item.id} className="rounded-xl overflow-hidden"
                            style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
                            <AutoScanPlayer
                              kamera={adhocKamera}
                              embeddingsRef={embeddingsRef}
                              scanInterval={3000}
                              scanDelayMs={idx * 600}
                              scanEnabled={true}
                            />
                          </div>
                        );
                      }
                      // Foto item → existing thumbnail composite
                      const hasThreat = item.result?.threats?.some(th => th.status === 'WANTED' || th.status === 'SUSPECT');
                      const borderColor = hasThreat ? '#ff2d55' : item.status === 'done' ? 'rgba(0,255,136,0.3)' : item.status === 'error' ? 'rgba(255,45,85,0.3)' : item.status === 'scanning' ? 'rgba(0,212,255,0.5)' : 'var(--border-dim)';
                      return (
                        <div key={item.id} className="rounded-xl overflow-hidden flex flex-col relative"
                          style={{ background: 'var(--color-card)', border: `1px solid ${borderColor}`, boxShadow: hasThreat ? '0 0 12px rgba(255,45,85,0.3)' : 'none' }}>
                          <div className="relative" style={{ height: 90 }}>
                            <img src={item.thumbUrl} alt={item.file.name} className="w-full h-full object-cover" />
                            {item.status === 'scanning' && (
                              <div className="absolute inset-0 flex items-center justify-center"
                                style={{ background: 'rgba(2,6,14,0.6)' }}>
                                <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} />
                              </div>
                            )}
                            {item.status === 'waiting' && (
                              <div className="absolute top-1.5 right-1.5">
                                <Clock size={10} color="var(--text-mute)" />
                              </div>
                            )}
                            {hasThreat && (
                              <div className="absolute top-0 left-0 right-0 flex items-center justify-center py-0.5 text-[7px] font-black animate-pulse"
                                style={{ background: 'rgba(255,45,85,0.85)', color: 'white' }}>
                                ⚠ {getIntelLabel(item.result.threats.find(t => t.status === 'WANTED' || t.status === 'SUSPECT')?.status, lang)}
                              </div>
                            )}
                          </div>
                          <div className="px-2 py-1.5">
                            <p className="text-[8px] font-bold truncate" style={{ color: 'var(--text-dim)' }}>{item.file.name}</p>
                            {item.result && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.result.known > 0 && (
                                  <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,212,255,0.12)', color: 'var(--color-cyan)' }}>
                                    {item.result.known} dikenal
                                  </span>
                                )}
                                {item.result.unknown > 0 && (
                                  <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,45,85,0.12)', color: '#ff2d55' }}>
                                    {item.result.unknown} asing
                                  </span>
                                )}
                                {item.result.vehicles > 0 && (
                                  <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,107,43,0.12)', color: '#ff6b2b' }}>
                                    {item.result.vehicles} kendaraan
                                  </span>
                                )}
                                {item.result.plates?.length > 0 && (
                                  <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full font-mono" style={{ background: 'rgba(255,214,10,0.12)', color: '#ffd60a' }}>
                                    {item.result.plates[0].plate || item.result.plates[0].text}
                                  </span>
                                )}
                              </div>
                            )}
                            {item.status === 'error' && (
                              <p className="text-[7px] mt-0.5" style={{ color: '#ff2d55' }}>{t('proc_failed')}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {source === 'camera' && (
            <div className="flex-1 relative rounded-2xl overflow-hidden" style={{ background: '#000', border: '1px solid var(--border-dim)', minHeight: 320 }}>
              {/* Live video feed */}
              <video ref={videoRef} autoPlay playsInline muted
                className="w-full h-full object-cover"
                style={{ display: imageLoaded && !autoScan && !cameraActive ? 'none' : 'block' }} />
              {/* Face mesh + bbox overlay — shown whenever camera is active */}
              <canvas ref={overlayRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ display: cameraActive ? 'block' : 'none' }} />
              {/* Static scan result canvas — shown after manual scan */}
              <canvas ref={canvasRef}
                className="w-full h-full object-contain"
                style={{ display: imageLoaded && !cameraActive ? 'block' : 'none' }} />

              {!cameraActive && !imageLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <Camera size={48} color="rgba(0,212,255,0.2)" strokeWidth={1} />
                  <button onClick={activateCamera} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black" style={{ background: 'var(--color-cyan)', color: '#02060e' }}>
                    <Play size={14} /> {t('activate_cam')}
                  </button>
                </div>
              )}

              {cameraActive && (
                <>
                  <div className="absolute inset-0 scan-lines pointer-events-none" />

                  {/* Status badges top-left */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                      style={{ background: 'rgba(2,6,14,0.85)', border: '1px solid rgba(255,45,85,0.3)' }}>
                      <div className="w-1.5 h-1.5 rounded-full animate-blink" style={{ background: '#ff2d55' }} />
                      <span className="text-[9px] font-black font-mono" style={{ color: '#ff2d55' }}>LIVE</span>
                    </div>
                    {autoScan && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                        style={{ background: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.4)' }}>
                        <div className="w-1.5 h-1.5 rounded-full animate-blink" style={{ background: 'var(--color-green)' }} />
                        <span className="text-[9px] font-black font-mono" style={{ color: 'var(--color-green)' }}>AUTO CCTV</span>
                      </div>
                    )}
                    {scanning && autoScan && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                        style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)' }}>
                        <div className="w-2.5 h-2.5 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} />
                        <span className="text-[9px] font-mono" style={{ color: 'var(--color-cyan)' }}>SCANNING</span>
                      </div>
                    )}
                  </div>

                  {/* Scan count top-right */}
                  {autoScan && (
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                      <div className="px-2 py-0.5 rounded-lg text-[8px] font-black font-mono"
                        style={{ background: 'rgba(2,6,14,0.85)', color: 'var(--color-green)' }}>
                        #{autoScanCount} scan
                      </div>
                      {lastScanTs && (
                        <div className="px-2 py-0.5 rounded-lg text-[7px] font-mono"
                          style={{ background: 'rgba(2,6,14,0.7)', color: 'var(--text-mute)' }}>
                          last: {lastScanTs}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Scan interval bar — green progress pulse when auto-scan on */}
                  {autoScan && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'rgba(0,255,136,0.15)' }}>
                      <div className="h-full animate-pulse" style={{ background: 'var(--color-green)', width: scanning ? '100%' : '0%', transition: 'width 0.3s' }} />
                    </div>
                  )}

                  {/* Controls bottom-center */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    <button
                      onClick={() => setAutoScan(v => !v)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black transition-all"
                      style={{
                        background: autoScan ? 'rgba(0,255,136,0.2)' : 'rgba(0,212,255,0.15)',
                        border: `1px solid ${autoScan ? 'rgba(0,255,136,0.5)' : 'rgba(0,212,255,0.4)'}`,
                        color: autoScan ? 'var(--color-green)' : 'var(--color-cyan)',
                      }}>
                      <div className={`w-1.5 h-1.5 rounded-full ${autoScan ? 'animate-blink' : ''}`}
                        style={{ background: autoScan ? 'var(--color-green)' : 'var(--color-cyan)' }} />
                      {autoScan ? (lang === 'id' ? 'CCTV OTOMATIS' : 'AUTO CCTV ON') : (lang === 'id' ? 'CCTV MANUAL' : 'AUTO CCTV OFF')}
                    </button>
                    {/* B-001: Refresh Feed — restart feeds + clear panels, tetap di Vision */}
                    <button
                      onClick={() => {
                        setDetections([]);
                        setSessionLog([]);
                        setIdentLog([]);
                        setScanning(false);
                        setScanProgress(0);
                        const wasAuto = autoScan;
                        setAutoScan(false);
                        addLog(lang === 'id' ? 'Feed di-refresh oleh operator' : 'Feed refreshed by operator', 'var(--color-cyan)');
                        if (wasAuto) setTimeout(() => setAutoScan(true), 800);
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
                      style={{ background: 'rgba(255,214,10,0.12)', border: '1px solid rgba(255,214,10,0.35)', color: '#ffd60a' }}
                      title={lang === 'id' ? 'Restart semua feed dan deteksi' : 'Restart all feeds and detection'}>
                      <RefreshCw size={10} /> {lang === 'id' ? 'Refresh Feed' : 'Refresh Feed'}
                    </button>
                    <button onClick={stopCamera}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black"
                      style={{ background: 'rgba(255,45,85,0.15)', border: '1px solid rgba(255,45,85,0.3)', color: '#ff2d55' }}>
                      <Square size={10} fill="#ff2d55" /> {lang === 'id' ? 'Hentikan' : 'Stop'}
                    </button>
                  </div>
                </>
              )}

              {imageLoaded && (
                <>
                  {scanning && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-xl"
                      style={{ background: 'rgba(2,6,14,0.9)', border: '1px solid rgba(0,212,255,0.3)' }}>
                      <div className="w-1.5 h-1.5 rounded-full animate-blink" style={{ background: 'var(--color-cyan)' }} />
                      <span className="text-[9px] font-black font-mono" style={{ color: 'var(--color-cyan)' }}>{t('scanning')} {scanProgress}%</span>
                    </div>
                  )}
                  {hfError && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-xl"
                      style={{ background: 'rgba(2,6,14,0.9)', border: '1px solid rgba(255,45,85,0.3)' }}>
                      <span className="text-[9px] font-black font-mono" style={{ color: '#ff2d55' }}>⚠ {hfError}</span>
                    </div>
                  )}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    <button onClick={() => { setImageLoaded(false); setDetections([]); setHfError(null); }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black"
                      style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.4)', color: 'var(--color-cyan)' }}>
                      <Eye size={11} /> {lang === 'id' ? 'Pindai Ulang' : 'Scan Again'}
                    </button>
                    <button onClick={stopCamera}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black"
                      style={{ background: 'rgba(255,45,85,0.15)', border: '1px solid rgba(255,45,85,0.3)', color: '#ff2d55' }}>
                      <Square size={10} fill="#ff2d55" /> {lang === 'id' ? 'Hentikan' : 'Stop'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 'stream' (Jaringan CCTV) di-merge ke 'feed' — hapus dari render */}

          {scanning && source !== 'batch' && (
            <div className="h-1 rounded-full overflow-hidden shrink-0" style={{ background: 'rgba(0,212,255,0.1)' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${scanProgress}%`, background: 'var(--color-cyan)' }} />
            </div>
          )}
        </div>

        {/* Right: Combined Log Aktivitas panel (merged) */}
        <div className="w-full h-56 lg:h-auto lg:w-72 shrink-0 flex flex-col">
          <div className="rounded-2xl flex flex-col overflow-hidden flex-1"
            style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
            {/* Header gabungan */}
            <div className="flex items-center justify-between px-3 py-2.5 shrink-0"
              style={{ borderBottom: '1px solid var(--border-lo)' }}>
              <p className="text-[10px] font-black uppercase tracking-widest font-mono"
                style={{ color: 'var(--color-cyan)' }}>
                {t('panel_log')}
              </p>
              <span className="text-[8px] font-mono px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
                {identLog.filter(p => p.type === 'identified').length + detections.filter(d => d.type !== 'WAJAH_DIKENAL' && d.type !== 'WAJAH_TIDAK_DIKENAL').length} {t('item_label')}
              </span>
            </div>

            {/* Section 1: Subjek Terdeteksi — 50% height, independent scroll */}
            <div className="flex flex-col flex-1 min-h-0">
            <div className="px-3 pt-2.5 pb-1 flex items-center justify-between shrink-0">
              <p className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
                {t('panel_subjects')}
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                  {sessionLog.length}/{SESSION_LOG_MAX} {t('session_label')}
                </span>
                {sessionLog.length > 0 && (
                  <button onClick={() => setSessionLog([])}
                    className="text-[7px] font-mono px-1 py-0.5 rounded hover:opacity-80"
                    style={{ background: 'rgba(255,45,85,0.10)', color: '#ff2d55', border: '1px solid rgba(255,45,85,0.25)' }}
                    title={t('clear_session_tip')}>
                    {t('clear_session')}
                  </button>
                )}
              </div>
            </div>
            {sessionLog.length === 0 ? (
              <p className="text-[9px] text-center py-2 px-3" style={{ color: 'var(--text-mute)' }}>
                {t('subjects_empty')}
              </p>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto c-scroll px-3 pb-2">
              <div className="flex flex-wrap gap-1.5">
                {sessionLog.map((det) => {
                  const cfg = {
                    WAJAH_DIKENAL:       { bg: 'rgba(0,212,255,0.10)',  bd: 'rgba(0,212,255,0.4)',  fg: 'var(--color-cyan)',  label: 'WAJAH DIKENAL' },
                    WAJAH_TIDAK_DIKENAL: { bg: 'rgba(255,45,85,0.10)',  bd: 'rgba(255,45,85,0.4)',  fg: '#ff2d55',            label: 'WAJAH TAK DIKENAL' },
                    KENDARAAN:           { bg: 'rgba(255,107,43,0.10)', bd: 'rgba(255,107,43,0.4)', fg: '#ff6b2b',            label: 'KENDARAAN' },
                    PLAT:                { bg: 'rgba(255,214,10,0.10)', bd: 'rgba(255,214,10,0.4)', fg: '#ffd60a',            label: 'PLAT' },
                    OBJEK:               { bg: 'rgba(148,163,184,0.10)',bd: 'rgba(148,163,184,0.3)',fg: 'var(--text-dim)',    label: 'OBJEK' },
                  }[det.type] || { bg: 'var(--color-bg)', bd: 'var(--border-lo)', fg: 'var(--text-mute)', label: det.type };
                  const overrideFg = det.intel === 'WANTED' ? '#ff2d55' : det.intel === 'SUSPECT' ? '#ff6b2b' : cfg.fg;
                  return (
                    <span key={det.id}
                      className="text-[8px] font-black font-mono px-2 py-1 rounded"
                      style={{ background: cfg.bg, border: `1px solid ${cfg.bd}`, color: overrideFg }}
                      title={`${det._hits || 1}× terdeteksi`}>
                      {cfg.label} · {det.conf}%
                      {(det._hits || 1) > 1 && <span style={{ opacity: 0.7, marginLeft: 4 }}>×{det._hits}</span>}
                    </span>
                  );
                })}
              </div>
              </div>
            )}
            </div>

            {/* Divider — splits Subjects (top 50%) dari Results (bottom 50%) */}
            <div className="shrink-0" style={{ borderTop: '1px solid rgba(0,212,255,0.2)' }} />

            {/* Section 2: Hasil Deteksi — full identity card (foto + KTP info untuk wajah,
                plat+merk+pemilik untuk kendaraan, dst). Render dari detections. */}
            {/* Section 2: Hasil Deteksi — 50% height, independent scroll */}
            <div className="flex flex-col flex-1 min-h-0">
                  <div className="px-3 pt-2.5 pb-1 flex items-center justify-between shrink-0">
                    <p className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
                      {t('det_result')}
                    </p>
                    <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                      {sessionLog.length} {t('item_label')}
                    </span>
                  </div>

                  {sessionLog.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-6 px-3 flex-1">
                      <Eye size={24} color="rgba(0,212,255,0.15)" strokeWidth={1} />
                      <p className="text-[10px] text-center" style={{ color: 'var(--text-mute)' }}>
                        {scanning ? `${t('scanning')}...` : t('det_empty')}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 overflow-y-auto c-scroll px-3 pb-3 flex-1 min-h-0">
                      {sessionLog.map(det => {
                  const Icon = typeIcon[det.type] || Eye;
                  const isSelected = selectedDet?.id === det.id;

                  if (det.type === 'WAJAH_DIKENAL') {
                    const isWanted  = det.intel === 'WANTED';
                    const isSuspect = det.intel === 'SUSPECT';
                    const statusColor = isWanted ? '#ff2d55' : isSuspect ? '#ff6b2b' : 'var(--color-green)';
                    const alamatFull = [det.alamat, det.rt_rw && `RT/RW ${det.rt_rw}`, det.kel_desa, det.kecamatan, det.kota].filter(Boolean).join(', ');
                    return (
                      <div key={det.id}
                        className="w-full text-left rounded-xl overflow-hidden cursor-pointer transition-opacity hover:opacity-90"
                        onClick={() => setSelectedIdent({
                          type: 'identified',
                          foto: det.foto_url,
                          nama: det.label,
                          status: det.intel || 'CLEAR',
                          firstSeenAt: det.detectedAt || det.ts,
                          penduduk: {
                            nama_lengkap: det.label,
                            nik: det.nik,
                            tempat_lahir: det.ttl ? det.ttl.split(',')[0]?.trim() : null,
                            tanggal_lahir: det.ttl ? det.ttl.split(',').slice(1).join(',').trim() : null,
                            gol_darah: det.gol_darah,
                            agama: det.agama,
                            status_perkawinan: det.status_kawin,
                            pekerjaan: det.pekerjaan,
                            kewarganegaraan: det.kewarganegaraan,
                            alamat_lengkap: [det.alamat, det.rt_rw && `RT/RW ${det.rt_rw}`, det.kel_desa, det.kecamatan, det.kota].filter(Boolean).join(', '),
                          },
                        })}
                        style={{ background: 'var(--color-surface)', border: `1px solid ${isWanted ? 'rgba(255,45,85,0.5)' : isSuspect ? 'rgba(255,107,43,0.4)' : 'var(--border-lo)'}`, boxShadow: isWanted ? '0 0 10px rgba(255,45,85,0.15)' : 'none' }}>
                        {/* Header strip */}
                        <div className="flex items-center gap-2 px-3 py-2"
                          style={{ background: isWanted ? 'rgba(255,45,85,0.12)' : isSuspect ? 'rgba(255,107,43,0.1)' : 'rgba(0,212,255,0.08)' }}>
                          <Eye size={12} color={det.color} />
                          <span className="text-[8px] font-black uppercase tracking-widest font-mono" style={{ color: det.color }}>WAJAH DIKENAL</span>
                          <span className="ml-auto text-[9px] font-black font-mono" style={{ color: det.color }}>{det.conf}%</span>
                        </div>

                        <div className="px-3 py-2.5 flex flex-col gap-1.5">
                          {/* Foto profil + Nama + status */}
                          <div className="flex items-start gap-2.5">
                            {det.foto_url ? (
                              <img src={det.foto_url} alt=""
                                className="rounded-xl object-cover shrink-0"
                                style={{ width: 52, height: 64, border: `2px solid ${statusColor}` }} />
                            ) : (
                              <div className="rounded-xl shrink-0 flex items-center justify-center"
                                style={{ width: 52, height: 64, background: 'var(--color-surface)', border: `2px solid ${statusColor}44` }}>
                                <Eye size={18} color={statusColor + '88'} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-1 mb-0.5">
                                <p className="text-[11px] font-black leading-tight" style={{ color: 'var(--text-hi)' }}>{det.label}</p>
                                <span className="shrink-0 text-[7px] font-black px-1.5 py-0.5 rounded-full"
                                  style={{ background: statusColor + '22', border: `1px solid ${statusColor}55`, color: statusColor }}>
                                  {getIntelLabel(det.intel || 'CLEAR', lang)}
                                </span>
                              </div>
                              {det.nik && (
                                <p className="text-[8px] font-mono" style={{ color: 'var(--text-dim)' }}>NIK: <span style={{ color: 'var(--text-mid)' }}>{det.nik}</span></p>
                              )}
                              {det.ttl && (
                                <p className="text-[8px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
                                  TTL: <span style={{ color: 'var(--text-mid)' }}>{det.ttl}</span>
                                </p>
                              )}
                              {det.umur != null && (
                                <p className="text-[8px] font-black mt-0.5" style={{ color: det.color }}>UMUR: {det.umur} TAHUN</p>
                              )}
                            </div>
                          </div>

                          {/* Separator */}
                          <div className="h-px" style={{ background: 'var(--border-dim)' }} />

                          {/* 2-col grid: semua field KTP */}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                            {[
                              { label: 'Jenis Kelamin', val: det.gender },
                              { label: 'Gol. Darah',    val: det.gol_darah },
                              { label: 'Agama',         val: det.agama },
                              { label: 'Status Kawin',  val: det.status_kawin },
                              { label: 'Pekerjaan',     val: det.pekerjaan },
                              { label: 'Kewarganegaraan', val: det.kewarganegaraan },
                            ].map(({ label, val }) => val ? (
                              <div key={label}>
                                <p className="text-[7px] uppercase tracking-wide" style={{ color: 'var(--text-mute)' }}>{label}</p>
                                <p className="text-[8px] font-bold" style={{ color: 'var(--text-mid)' }}>{val}</p>
                              </div>
                            ) : null)}
                          </div>

                          {/* Alamat */}
                          {alamatFull && (
                            <div>
                              <p className="text-[7px] uppercase tracking-wide" style={{ color: 'var(--text-mute)' }}>Alamat</p>
                              <p className="text-[8px] leading-relaxed" style={{ color: 'var(--text-mid)' }}>{alamatFull}</p>
                            </div>
                          )}

                          {/* Catatan fisik */}
                          {det.catatan && (
                            <div>
                              <p className="text-[7px] uppercase tracking-wide" style={{ color: 'var(--text-mute)' }}>Catatan Fisik</p>
                              <p className="text-[8px]" style={{ color: 'var(--text-mid)' }}>{det.catatan}</p>
                            </div>
                          )}

                          {/* Last seen tracking */}
                          {(det.last_seen || det.lokasi_last) && (
                            <div className="rounded-lg px-2 py-1.5" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
                              <p className="text-[7px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--color-cyan)' }}>{t('last_seen')}</p>
                              {det.lokasi_last && <p className="text-[8px]" style={{ color: 'var(--text-mid)' }}>{det.lokasi_last}</p>}
                              {det.last_seen  && <p className="text-[7px] font-mono mt-0.5" style={{ color: 'var(--text-mute)' }}>{new Date(det.last_seen).toLocaleString('id-ID')}</p>}
                            </div>
                          )}

                          {/* WANTED/SUSPECT alert */}
                          {(isWanted || isSuspect) && (
                            <div className="rounded-lg px-2 py-1.5" style={{ background: 'rgba(255,45,85,0.12)', border: '1px solid rgba(255,45,85,0.35)' }}>
                              <p className="text-[8px] font-black" style={{ color: '#ff2d55' }}>⚠ STATUS: {getIntelLabel(det.intel, lang)}</p>
                              <p className="text-[8px] mt-0.5" style={{ color: '#ff6b7a' }}>Tindakan: Amankan segera</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // KENDARAAN card — klik untuk buka VehiclePopup detail.
                  // Selain itu (PLAT, OBJEK), tetap pakai toggle highlight selectedDet.
                  const isVehicle = det.type === 'KENDARAAN';
                  const stnkStatus = det.vehicle?.status_stnk;
                  const stnkColor = stnkStatus === 'AKTIF' ? 'var(--color-green)'
                                  : stnkStatus === 'EXPIRED' ? '#ff2d55'
                                  : stnkStatus === 'BLOKIR' ? '#ff6b2b' : null;
                  return (
                    <button key={det.id}
                      onClick={() => isVehicle && det.vehicle ? setSelectedVehicle(det) : setSelectedDet(isSelected ? null : det)}
                      className="w-full text-left rounded-xl p-3 transition-all"
                      style={{ background: isSelected ? det.color + '14' : 'var(--color-surface)', border: `1px solid ${isSelected ? det.color + '44' : 'var(--border-lo)'}` }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon size={13} color={det.color} />
                        <span className="text-[9px] font-black uppercase tracking-wider font-mono" style={{ color: det.color }}>
                          {typeLabel[det.type] || det.type}
                        </span>
                        {stnkColor && (
                          <span className="text-[7px] font-black px-1.5 py-0.5 rounded font-mono"
                            style={{ background: stnkColor + '22', border: `1px solid ${stnkColor}55`, color: stnkColor }}>
                            STNK {stnkStatus}
                          </span>
                        )}
                        <span className="ml-auto text-[9px] font-black font-mono" style={{ color: det.color }}>{det.conf}%</span>
                      </div>
                      <p className="text-xs font-bold" style={{ color: 'var(--text-hi)' }}>{det.label}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-dim)' }}>{det.sub}</p>
                    </button>
                  );
                })}
                    </div>
                  )}
            </div>
          </div>

          {/* C-004 PENDING_PM_APPROVAL: System Log dipindah ke Datacenter admin tab.
              Panel ini di-hide dari Vision (tidak ada gap — block langsung removed).
              Logs tetap ter-accumulate di state (addLog() masih aktif) untuk
              dibaca Datacenter via prop drilling atau Supabase log table nanti. */}
        </div>
      </div>
      )} {/* end source !== 'feed' */}

      {/* Feature 001 — Detail Popup (saat klik Identified Card) */}
      {selectedIdent && selectedIdent.type === 'identified' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setSelectedIdent(null)}>
          <div className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
            style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '88vh' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 shrink-0"
              style={{ borderBottom: '1px solid var(--border-dim)' }}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Eye size={14} color="var(--color-cyan)" />
                <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
                  Detail Subjek Teridentifikasi
                </p>
              </div>
              {/* § 4.1: Reach Subject Profiling dari People Detection Result card */}
              {selectedIdent.penduduk?.nik && (
                <button
                  onClick={() => {
                    sessionStorage.setItem('open_warga_nik', selectedIdent.penduduk.nik);
                    setSelectedIdent(null);
                    setPage('neointel');
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black transition-all hover:opacity-90 mr-2"
                  style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.4)', color: 'var(--color-cyan)' }}
                  title={lang === 'id' ? 'Buka Subject Profiling + OSINT' : 'Open Subject Profiling + OSINT'}>
                  <User size={11} /> {lang === 'id' ? 'Subject Profiling' : 'Subject Profiling'}
                </button>
              )}
              <button onClick={() => setSelectedIdent(null)}
                className="rounded-lg p-1 hover:opacity-70 transition-all">
                <X size={13} color="var(--text-dim)" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto c-scroll p-6 space-y-5">
              {/* Section 1 — Photos */}
              <div>
                <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2" style={{ color: 'var(--color-cyan)' }}>
                  Foto
                </p>
                <div className="flex gap-3">
                  {selectedIdent.foto ? (
                    <img src={selectedIdent.foto} alt={selectedIdent.nama}
                      className="rounded-xl object-cover" style={{ width: 140, height: 180, border: '2px solid rgba(0,212,255,0.3)' }} />
                  ) : (
                    <div className="rounded-xl flex items-center justify-center"
                      style={{ width: 140, height: 180, background: 'var(--color-surface)', border: '2px dashed var(--border-dim)' }}>
                      <Eye size={28} color="rgba(148,163,184,0.3)" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-black mb-1" style={{ color: 'var(--text-hi)' }}>
                      {selectedIdent.nama || '—'}
                    </p>
                    {(() => {
                      const sFg = ({ WANTED: '#ff2d55', SUSPECT: '#ff6b2b', WATCHLIST: '#ffd60a', CLEAR: 'var(--color-green)' })[selectedIdent.status] || 'var(--color-green)';
                      return (
                        <span className="inline-block text-[9px] font-black font-mono px-2 py-0.5 rounded"
                          style={{ background: sFg + '22', border: `1px solid ${sFg}55`, color: sFg }}>
                          {getIntelLabel(selectedIdent.status, lang)}
                        </span>
                      );
                    })()}
                    <p className="text-[10px] font-mono mt-2" style={{ color: 'var(--text-dim)' }}>
                      Pertama terdeteksi: {selectedIdent.firstSeenAt ? new Date(selectedIdent.firstSeenAt).toLocaleTimeString('id-ID') : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 2 — Documents (placeholder, belum ada di schema) */}
              <div>
                <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2" style={{ color: 'var(--color-cyan)' }}>
                  Dokumen
                </p>
                <p className="text-[10px] italic" style={{ color: 'var(--text-mute)' }}>
                  KTP / Passport / Akta Lahir / KK belum tersedia di database — placeholder.
                </p>
              </div>

              {/* Section 3 — Personal Data (SEMUA field SELALU tampil) */}
              <div>
                <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-3" style={{ color: 'var(--color-cyan)' }}>
                  Data Personal
                </p>
                {(() => {
                  const p = selectedIdent.penduduk || {};
                  const namaParts = (p.nama_lengkap || '').split(' ');
                  const namaDepan = namaParts[0] || '—';
                  const namaBelakang = namaParts.slice(1).join(' ') || '—';
                  const calcAge = (tgl) => {
                    if (!tgl) return '—';
                    const b = new Date(tgl);
                    if (isNaN(b)) return '—';
                    const t = new Date();
                    let age = t.getFullYear() - b.getFullYear();
                    if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
                    return `${age} tahun`;
                  };
                  const ttl = p.tempat_lahir && p.tanggal_lahir ? `${p.tempat_lahir}, ${p.tanggal_lahir}` : (p.tempat_lahir || p.tanggal_lahir || '—');
                  const fields = [
                    { label: 'Nama Depan',         value: namaDepan },
                    { label: 'Nama Belakang',      value: namaBelakang },
                    { label: 'NIK',                value: p.nik || '—' },
                    { label: 'Nomor Passport',     value: p.passport_number || '—' },
                    { label: 'Usia',               value: calcAge(p.tanggal_lahir) },
                    { label: 'Tempat & Tgl Lahir', value: ttl },
                    { label: 'Golongan Darah',     value: p.gol_darah || '—' },
                    { label: 'Agama',              value: p.agama || '—' },
                    { label: 'Status Perkawinan',  value: p.status_perkawinan || '—' },
                    { label: 'Pekerjaan',          value: p.pekerjaan || '—' },
                    { label: 'Alamat Lengkap',     value: p.alamat_lengkap || '—' },
                  ];
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      {fields.map((f, i) => (
                        <div key={i} className={f.label === 'Alamat Lengkap' ? 'col-span-2' : ''}>
                          <p className="text-[8px] uppercase tracking-widest font-mono mb-0.5" style={{ color: 'var(--text-mute)' }}>
                            {f.label}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-mid)' }}>{f.value}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Section 4 — Dokumen Identitas */}
              {(() => {
                const p = selectedIdent.penduduk || {};
                const docs = [
                  { key: 'dokumen_ktp_url',      label: 'KTP'             },
                  { key: 'dokumen_passport_url', label: 'Paspor'          },
                  { key: 'dokumen_akta_url',     label: 'Akta Lahir'      },
                  { key: 'dokumen_kk_url',       label: 'Kartu Keluarga'  },
                ];
                const available = docs.filter(d => p[d.key]);
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <p className="text-[8px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--color-cyan)' }}>
                        Dokumen Identitas
                      </p>
                      <span className="text-[7px] font-mono px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
                        {available.length}/{docs.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {docs.map(d => {
                        const url = p[d.key];
                        if (url) {
                          return (
                            <a key={d.key} href={url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:opacity-80 transition-opacity"
                              style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.3)' }}>
                              <FileText size={14} color="var(--color-cyan)" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold" style={{ color: 'var(--text-hi)' }}>{d.label}</p>
                                <p className="text-[8px] font-mono" style={{ color: 'var(--color-cyan)' }}>{t('open_file')}</p>
                              </div>
                            </a>
                          );
                        }
                        return (
                          <div key={d.key} className="flex items-center gap-2 px-2.5 py-2 rounded-lg"
                            style={{ background: 'var(--color-surface)', border: '1px dashed var(--border-dim)', opacity: 0.5 }}>
                            <FileText size={14} color="var(--text-mute)" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-bold" style={{ color: 'var(--text-mute)' }}>{d.label}</p>
                              <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>{t('no_data')}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* HORUS V2 14.2 — Vehicle Detail Popup
          Open saat user klik card kendaraan di panel deteksi.
          Tampilkan: foto kendaraan, data STNK + dokumen, identitas pemilik,
          link buka di Subject Profiling. */}
      {selectedVehicle && selectedVehicle.vehicle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setSelectedVehicle(null)}>
          <div className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
            style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '88vh' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 shrink-0"
              style={{ borderBottom: '1px solid var(--border-dim)' }}>
              <div className="flex items-center gap-2">
                <Car size={14} color="#ff6b2b" />
                <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
                  {lang === 'id' ? 'Detail Kendaraan' : 'Vehicle Detail'}
                </p>
              </div>
              <button onClick={() => setSelectedVehicle(null)}
                className="rounded-lg p-1 hover:opacity-70 transition-all">
                <X size={13} color="var(--text-dim)" />
              </button>
            </div>

            {(() => {
              const v = selectedVehicle.vehicle;
              const p = v.pemilik || null;
              const stnkStatus = v.status_stnk || (v.masa_berlaku_stnk && new Date(v.masa_berlaku_stnk) < new Date() ? 'EXPIRED' : 'AKTIF');
              const stnkColor = stnkStatus === 'AKTIF' ? 'var(--color-green)'
                              : stnkStatus === 'EXPIRED' ? '#ff2d55'
                              : '#ff6b2b';
              const intelColor = ({ WANTED: '#ff2d55', SUSPECT: '#ff6b2b', WATCHLIST: '#ffd60a', CLEAR: 'var(--color-green)' })[p?.status_intelijen] || 'var(--color-green)';
              return (
              <div className="flex-1 overflow-y-auto c-scroll p-6 space-y-5">
                {/* Section 1 — Foto kendaraan + plat besar */}
                <div className="flex gap-4">
                  {v.foto_url ? (
                    <img src={v.foto_url} alt={v.no_plat}
                      className="rounded-xl object-cover" style={{ width: 140, height: 100, border: '2px solid #ff6b2b55' }} />
                  ) : (
                    <div className="rounded-xl flex items-center justify-center"
                      style={{ width: 140, height: 100, background: 'var(--color-surface)', border: '2px dashed var(--border-dim)' }}>
                      <Car size={28} color="rgba(255,107,43,0.3)" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-2xl font-black font-mono" style={{ color: 'var(--text-hi)' }}>{v.no_plat}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-dim)' }}>
                      {[v.merk_model, v.warna, v.tahun].filter(Boolean).join(' · ') || '—'}
                    </p>
                    <span className="inline-block mt-2 text-[9px] font-black font-mono px-2 py-0.5 rounded"
                      style={{ background: stnkColor + '22', border: `1px solid ${stnkColor}55`, color: stnkColor }}>
                      STNK {stnkStatus}
                    </span>
                  </div>
                </div>

                {/* Section 2 — Data Kendaraan */}
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2" style={{ color: '#ff6b2b' }}>
                    {lang === 'id' ? 'Data Kendaraan' : 'Vehicle Data'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { l: lang === 'id' ? 'Plat' : 'Plate',         val: v.no_plat },
                      { l: lang === 'id' ? 'Jenis' : 'Type',         val: v.tipe_kendaraan },
                      { l: lang === 'id' ? 'Merek/Model' : 'Make/Model', val: v.merk_model },
                      { l: lang === 'id' ? 'Tahun' : 'Year',         val: v.tahun },
                      { l: lang === 'id' ? 'Warna' : 'Color',        val: v.warna },
                      { l: lang === 'id' ? 'No. Rangka' : 'Chassis', val: v.no_rangka },
                      { l: lang === 'id' ? 'No. Mesin' : 'Engine',   val: v.no_mesin },
                    ].map((f, i) => (
                      <div key={i}>
                        <p className="text-[8px] uppercase tracking-widest font-mono mb-0.5" style={{ color: 'var(--text-mute)' }}>{f.l}</p>
                        <p className="text-xs font-mono" style={{ color: 'var(--text-mid)' }}>{f.val ?? '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 3 — Dokumen Registrasi (STNK + TNKB + BPKB) */}
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2" style={{ color: '#ff6b2b' }}>
                    {lang === 'id' ? 'Dokumen Registrasi' : 'Registration Documents'}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {[
                      { l: lang === 'id' ? 'No. Registrasi' : 'Reg. No.',  val: v.no_registrasi },
                      { l: lang === 'id' ? 'Tgl Terbit' : 'Issue Date',    val: v.tgl_registrasi },
                      { l: lang === 'id' ? 'Masa Berlaku' : 'Valid Until', val: v.masa_berlaku_stnk },
                      { l: lang === 'id' ? 'Status STNK' : 'STNK Status',  val: stnkStatus, color: stnkColor },
                    ].map((f, i) => (
                      <div key={i}>
                        <p className="text-[8px] uppercase tracking-widest font-mono mb-0.5" style={{ color: 'var(--text-mute)' }}>{f.l}</p>
                        <p className="text-xs font-mono" style={{ color: f.color || 'var(--text-mid)', fontWeight: f.color ? 'bold' : 'normal' }}>{f.val ?? '—'}</p>
                      </div>
                    ))}
                  </div>
                  {/* TNKB + BPKB photos */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[8px] uppercase tracking-widest font-mono mb-1" style={{ color: 'var(--text-mute)' }}>
                        {lang === 'id' ? 'Foto TNKB' : 'TNKB Photo'}
                      </p>
                      {v.tnkb_url ? (
                        <img src={v.tnkb_url} alt="TNKB" className="rounded-lg w-full object-cover"
                          style={{ height: 80, border: '1px solid var(--border-dim)' }} />
                      ) : (
                        <div className="rounded-lg flex items-center justify-center"
                          style={{ height: 80, background: 'var(--color-surface)', border: '1px dashed var(--border-dim)' }}>
                          <p className="text-[9px]" style={{ color: 'var(--text-mute)' }}>—</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[8px] uppercase tracking-widest font-mono mb-1" style={{ color: 'var(--text-mute)' }}>
                        {lang === 'id' ? 'Foto BPKB' : 'BPKB Photo'}
                      </p>
                      {v.bpkb_url ? (
                        <img src={v.bpkb_url} alt="BPKB" className="rounded-lg w-full object-cover"
                          style={{ height: 80, border: '1px solid var(--border-dim)' }} />
                      ) : (
                        <div className="rounded-lg flex items-center justify-center"
                          style={{ height: 80, background: 'var(--color-surface)', border: '1px dashed var(--border-dim)' }}>
                          <p className="text-[9px]" style={{ color: 'var(--text-mute)' }}>—</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 4 — Pemilik Terdaftar */}
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest font-mono mb-2" style={{ color: '#ff6b2b' }}>
                    {lang === 'id' ? 'Pemilik Terdaftar' : 'Registered Owner'}
                  </p>
                  {p ? (
                    <div className="flex items-start gap-3 p-3 rounded-xl"
                      style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                      {p.foto_url ? (
                        <img src={p.foto_url} alt={p.nama_lengkap}
                          className="rounded-xl object-cover shrink-0"
                          style={{ width: 60, height: 75, border: `2px solid ${intelColor}` }} />
                      ) : (
                        <div className="rounded-xl shrink-0 flex items-center justify-center"
                          style={{ width: 60, height: 75, background: 'var(--color-bg)', border: `2px solid ${intelColor}55` }}>
                          <span className="text-lg font-black" style={{ color: intelColor }}>
                            {(p.nama_lengkap || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black" style={{ color: 'var(--text-hi)' }}>{p.nama_lengkap || '—'}</p>
                        <p className="text-[9px] font-mono mt-0.5" style={{ color: 'var(--text-dim)' }}>
                          NIK: {p.nik ? p.nik.slice(0, 4) + '*'.repeat(8) + p.nik.slice(-4) : '—'}
                        </p>
                        <span className="inline-block mt-1.5 text-[9px] font-black font-mono px-2 py-0.5 rounded"
                          style={{ background: intelColor + '22', border: `1px solid ${intelColor}55`, color: intelColor }}>
                          {getIntelLabel(p.status_intelijen || 'CLEAR', lang)}
                        </span>
                        <button
                          onClick={() => {
                            // REG-003: pass owner NIK ke NeoIntel supaya auto-buka subject spesifik
                            if (p.nik) sessionStorage.setItem('open_warga_nik', p.nik);
                            setSelectedVehicle(null);
                            setPage('neointel');
                          }}
                          className="mt-2 ml-3 inline-flex items-center gap-1 text-[9px] font-black font-mono px-2 py-1 rounded transition-all hover:opacity-80"
                          style={{ background: 'rgba(0,212,255,0.10)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
                          {lang === 'id' ? 'Buka Subject Profiling →' : 'Open Subject Profiling →'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[10px] italic" style={{ color: 'var(--text-mute)' }}>
                      {lang === 'id' ? 'Pemilik tidak terdaftar di basis data.' : 'Owner not registered in database.'}
                    </p>
                  )}
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default NeoVision;
